import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { 
  HiCash, 
  HiCurrencyDollar, 
  HiCube, 
  HiArrowUp,
  HiCalendar,
  HiCheck,
  HiChevronLeft,
  HiChevronRight,
  HiChevronDown,
  HiChevronUp,
  HiChartBar,
  HiX,
  HiArrowDown,
  HiPause,
  HiPlay,
  HiClock,
  HiCog,
  HiFilter,
  HiPlus,
  HiEye,
  HiCursorClick,
  HiShoppingCart,
} from 'react-icons/hi';
import apiClient from '../api/client';
import ModalOverlay from '../components/ModalOverlay';
import CreateCampaignModal from '../components/CreateCampaignModal';
import { useNotification } from '../context/NotificationContext';
import AutomationControlModal from '../components/AutomationControlModal';
import { patchCampaignWithStrategySettings } from '../utils/bidderSettingsPatch';
import ChartPanel, { ChartErrorBoundary } from '../components/ChartPanel';
import {
  MonthlyBudgetModal,
  StrategyModal,
  ScheduleModal,
  BulkEditPanel,
  statusMap,
} from './BidderTargets';
import HeatmapPage from './HeatmapPage';

const metricOptions = [
  { key: 'views', label: 'Показы', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'clicks', label: 'Клики', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'spend', label: 'Расход, ₽', format: (value) =>
      value !== null ? value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0' },
  { key: 'orders', label: 'Заказы', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'atbs', label: 'Корзины', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'revenue', label: 'Выручка, ₽', format: (value) =>
      value !== null ? value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0' },
  { key: 'ctr', label: 'CTR, %', format: (value) => (value !== null ? `${value.toFixed(2)}%` : '0%') },
  { key: 'cpc', label: 'CPC, ₽', format: (value) => (value !== null ? value.toFixed(2) : '0.00') },
  { key: 'cpo', label: 'CPO, ₽', format: (value) => (value !== null ? value.toFixed(2) : '—') },
  { key: 'cart_cost', label: 'Стоимость корзины, ₽', format: (value) => (value !== null ? value.toFixed(2) : '—') },
  { key: 'avg_position', label: 'Позиция показа', format: (value) => (value != null ? Number(value).toFixed(2) : '—') },
  { key: 'drr', label: 'ДРР, %', format: (value) => (value != null ? `${Number(value).toFixed(2)}%` : '—') },
];

const groupingOptions = [
  { value: 'day', label: 'По дням' },
  { value: 'week', label: 'По неделям' },
  { value: 'month', label: 'По месяцам' },
];

const metricColors = {
  views: '#4f46e5',
  clicks: '#ef4444',
  spend: '#3b82f6',
  orders: '#10b981',
  atbs: '#7c3aed',
  revenue: '#f97316',
  ctr: '#6366f1',
  cpc: '#0ea5e9',
  cpo: '#ec4899',
  cart_cost: '#be185d',
  avg_position: '#a855f7',
  drr: '#14b8a6',
};

const tableGroupingOptions = [
  { key: 'campaign', label: 'Кампания' },
  { key: 'placement', label: 'Зона показа' },
  { key: 'norm_query', label: 'Поисковый запрос' },
  { key: 'nm_id', label: 'Артикул' },
  { key: 'skleyka', label: 'Склейка' },
];

const PLACEMENT_SEARCH = 'Поиск';
const PLACEMENT_RECOMMENDATIONS = 'Рекомендации';

/** Доля поиска в кампании по заказам (fallback — расход, затем показы). */
function computeSearchPlacementShare(searchTotals, campaignTotals) {
  if ((campaignTotals.orders || 0) > 0) {
    return Math.min(1, Math.max(0, (searchTotals.orders || 0) / campaignTotals.orders));
  }
  if ((campaignTotals.spend || 0) > 0) {
    return Math.min(1, Math.max(0, (searchTotals.spend || 0) / campaignTotals.spend));
  }
  if ((campaignTotals.views || 0) > 0) {
    return Math.min(1, Math.max(0, (searchTotals.views || 0) / campaignTotals.views));
  }
  return 0;
}

/** Масштабирует метрику в строках поиска, сохраняя пропорции (для согласованного split с рекомендациями). */
function scaleFactsMetric(facts, metricKey, targetTotal) {
  if (!facts.length) return;
  const target = Number(targetTotal) || 0;
  if (target <= 0) {
    facts.forEach((f) => {
      f[metricKey] = 0;
    });
    return;
  }
  const current = facts.reduce((s, f) => s + (Number(f[metricKey]) || 0), 0);
  if (current <= 0) {
    const weight = facts.reduce((s, f) => s + (Number(f.spend) || Number(f.orders) || 1), 0);
    if (weight <= 0) {
      const even = target / facts.length;
      facts.forEach((f) => {
        f[metricKey] = even;
      });
      return;
    }
    facts.forEach((f) => {
      const w = Number(f.spend) || Number(f.orders) || 1;
      f[metricKey] = (target * w) / weight;
    });
    return;
  }
  if (Math.abs(current - target) < 0.01) return;
  const factor = target / current;
  facts.forEach((f) => {
    f[metricKey] = (Number(f[metricKey]) || 0) * factor;
  });
}

const tableMetricOptions = [
  { key: 'views', label: 'Показы' },
  { key: 'clicks', label: 'Клики' },
  { key: 'spend', label: 'Расход' },
  { key: 'orders', label: 'Заказы' },
  { key: 'direct_orders', label: 'Прямые заказы' },
  { key: 'associated_orders', label: 'Ассоциированные заказы' },
  { key: 'atbs', label: 'В корзину' },
  { key: 'revenue', label: 'Доход' },
  { key: 'direct_revenue', label: 'Прямой доход' },
  { key: 'associated_revenue', label: 'Ассоциированный доход' },
  { key: 'ctr', label: 'CTR' },
  { key: 'cpc', label: 'CPC' },
  { key: 'cpo', label: 'CPO' },
  { key: 'cart_cost', label: 'Цена корзины' },
  { key: 'avg_position', label: 'Позиция' },
  { key: 'drr', label: 'ДРР' },
];

/** Метрики, где рост к периоду сравнения — ухудшение */
const LOWER_IS_BETTER_COMPARE_METRICS = new Set(['cpo', 'cart_cost', 'drr']);

const BUDGET_PIE_COLORS = {
  Потрачено: '#6366f1',
  Прогноз: '#f59e0b',
  Остаток: '#e2e8f0',
  Превышение: '#ef4444',
};

const SUMMARY_LEGEND_CARD_CLASS = 'bg-white rounded-lg shadow-sm border border-gray-100 px-4 py-3.5 hover:shadow-md transition-shadow h-full flex flex-col min-h-[92px]';
const BUDGET_LEGEND_CARD_CLASS = 'bg-white rounded-lg shadow-sm border border-gray-100 px-3 py-2 hover:shadow-md transition-shadow';
const SUMMARY_LEGEND_LABEL_CLASS = 'text-xs font-medium text-gray-600';
const SUMMARY_LEGEND_VALUE_CLASS = 'text-lg font-bold text-gray-900 mb-1';
const BUDGET_LEGEND_VALUE_CLASS = 'text-base font-bold text-gray-900 mb-1';

/**
 * Прогноз расхода на конец месяца.
 * Основной: spent + Σ дневных лимитов × оставшиеся дни (как реально целится распределение).
 * Темп (pace): spent / прошедшие дни × дней в месяце — «если не сбавлять», часто завышен.
 */
function calcMonthlyBudgetForecast({ spent, monthKey, dailyCapSum = null, totalBudget = null }) {
  const spentNum = Number(spent) || 0;
  if (!monthKey || spentNum < 0) return null;

  const [year, month] = monthKey.split('-').map(Number);
  if (!year || !month) return null;

  const daysInMonth = new Date(year, month, 0).getDate();
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() === month - 1;

  if (!isCurrentMonth) {
    return now > new Date(year, month - 1, daysInMonth, 23, 59, 59)
      ? { forecast: spentNum, paceForecast: spentNum, remainingDays: 0, mode: 'closed' }
      : null;
  }

  const daysElapsed = now.getDate();
  const remainingDays = Math.max(1, daysInMonth - daysElapsed + 1);
  const paceForecast = daysElapsed > 0 ? (spentNum / daysElapsed) * daysInMonth : null;

  const caps = Number(dailyCapSum);
  let forecast;
  let mode;
  if (Number.isFinite(caps) && caps >= 0) {
    forecast = spentNum + caps * remainingDays;
    mode = 'limits';
  } else if (totalBudget != null && Number(totalBudget) > 0) {
    // Нет лимитов в ответе — ожидаем, что автораспределение доведёт до плана
    forecast = Number(totalBudget);
    mode = 'plan';
  } else {
    forecast = paceForecast;
    mode = 'pace';
  }

  return {
    forecast: forecast != null ? Math.round(forecast) : null,
    paceForecast: paceForecast != null ? Math.round(paceForecast) : null,
    remainingDays,
    dailyCapSum: Number.isFinite(caps) ? caps : null,
    mode,
  };
}

/** Закреплённые столбцы: чекбокс … Остаток */
const PINNED_COL_COUNT = 8;
const PINNED_COL_WIDTHS = [48, 200, 80, 80, 90, 90, 140, 88];

function getPinnedLeft(colIndex) {
  if (colIndex <= 0) return 0;
  return PINNED_COL_WIDTHS.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
}

function pinnedCellProps(colIndex, className = '', { header = false, bg = 'bg-white/95' } = {}) {
  if (colIndex >= PINNED_COL_COUNT) {
    return { className };
  }
  const isLast = colIndex === PINNED_COL_COUNT - 1;
  const z = header ? 'z-20 top-0' : 'z-[8]';
  return {
    className: [
      className,
      'sticky',
      z,
      bg,
      isLast ? 'border-r border-slate-200 shadow-[4px_0_6px_-4px_rgba(15,23,42,0.12)]' : '',
    ].filter(Boolean).join(' '),
    style: {
      left: getPinnedLeft(colIndex),
      width: PINNED_COL_WIDTHS[colIndex],
      minWidth: PINNED_COL_WIDTHS[colIndex],
      maxWidth: PINNED_COL_WIDTHS[colIndex],
    },
  };
}

function TableColumnFilter({ label, active, open, onToggle, align = 'left', children }) {
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1 hover:text-gray-800 transition-colors w-full ${alignClass}`}
      >
        <span>{label}</span>
        <HiFilter className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 min-w-[260px] max-w-[340px] bg-white border border-slate-200 rounded-xl shadow-lg p-3 z-40 normal-case font-normal text-left">
          {children}
        </div>
      )}
    </div>
  );
}

/** Токены фильтра кампаний: список через пробел / запятую / перевод строки. */
function parseCampaignFilterTokens(value) {
  if (!value) return [];
  const seen = new Set();
  const tokens = [];
  for (const raw of String(value).split(/[\s,;]+/g)) {
    const t = raw.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tokens.push(t);
  }
  return tokens;
}

function campaignMatchesFilterToken(campaign, token, articles, vendorToNm) {
  const t = String(token || '').trim().toLowerCase();
  if (!t) return false;
  const name = String(campaign?.name || '').toLowerCase();
  const advertId = Number(campaign?.id ?? campaign?.advert_id);
  const advertIdStr = Number.isFinite(advertId) ? String(advertId) : '';
  const promotedNm = Number(campaign?.promoted_nm_id);
  const nmIds = articles?.nmIds || [];
  const vendors = articles?.vendorCodes || [];

  // Название кампании / номер кампании
  if (name.includes(t)) return true;
  if (/^\d+$/.test(t) && advertIdStr === t) return true;

  // Артикул WB (точное совпадение)
  if (/^\d+$/.test(t)) {
    const n = Number(t);
    if (Number.isFinite(promotedNm) && promotedNm === n) return true;
    if (nmIds.some((id) => Number(id) === n)) return true;
    if (name.includes(t)) return true;
  }

  // Артикул продавца — только точное совпадение (не substring)
  if (vendors.some((v) => String(v).toLowerCase() === t)) return true;

  // vendor → nm → продвигаемый / привязанный артикул / название
  const nmFromVendor = vendorToNm instanceof Map ? vendorToNm.get(t) : null;
  if (nmFromVendor) {
    if (Number.isFinite(promotedNm) && promotedNm === nmFromVendor) return true;
    if (nmIds.some((id) => Number(id) === nmFromVendor)) return true;
    if (name.includes(String(nmFromVendor))) return true;
  }

  return false;
}

const getCompareDeltaColorClass = (metricKey, delta) => {
  const isPositive = delta >= 0;
  if (LOWER_IS_BETTER_COMPARE_METRICS.has(metricKey)) {
    return isPositive ? 'text-red-600' : 'text-green-600';
  }
  return isPositive ? 'text-green-600' : 'text-red-600';
};

// Локальная дата YYYY-MM-DD (без UTC), чтобы «сегодня» и период совпадали с календарём пользователя
const formatDateInput = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseDateFromString = (value, fallback) => {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const defaultRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return {
    from: formatDateInput(from),
    to: formatDateInput(to),
  };
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear()}`;
};

const formatDateRange = (from, to) => {
  if (!from || !to) return '';
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  
  if (fromDate.getMonth() === toDate.getMonth() && fromDate.getFullYear() === toDate.getFullYear()) {
    return `${fromDate.getDate()} - ${toDate.getDate()} ${months[toDate.getMonth()]}, ${toDate.getFullYear()}`;
  }
  return `${fromDate.getDate()} ${months[fromDate.getMonth()]} - ${toDate.getDate()} ${months[toDate.getMonth()]}, ${toDate.getFullYear()}`;
};

/** Локальная дата из YYYY-MM-DD без сдвига UTC */
const parseLocalYmd = (value, fallback = new Date()) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return fallback instanceof Date ? new Date(fallback) : new Date();
  }
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const dayDiffInclusive = (fromDate, toDate) =>
  Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));

/** Сразу до основного периода, той же длины */
const computePreviousPeriod = (fromStr, toStr) => {
  const fromDate = parseLocalYmd(fromStr);
  const toDate = parseLocalYmd(toStr);
  const lengthMs = toDate.getTime() - fromDate.getTime();
  const compareTo = new Date(fromDate);
  compareTo.setDate(compareTo.getDate() - 1);
  const compareFrom = new Date(compareTo);
  compareFrom.setTime(compareFrom.getTime() - lengthMs);
  return { from: formatDateInput(compareFrom), to: formatDateInput(compareTo) };
};

/** Та же длина, сдвинутая ровно на 7 дней (те же дни недели) */
const computePreviousWeek = (fromStr, toStr) => {
  const fromDate = parseLocalYmd(fromStr);
  const toDate = parseLocalYmd(toStr);
  fromDate.setDate(fromDate.getDate() - 7);
  toDate.setDate(toDate.getDate() - 7);
  return { from: formatDateInput(fromDate), to: formatDateInput(toDate) };
};

/**
 * Предыдущий месяц: сдвиг на календарный месяц с выравниванием по дням недели
 * (пн–вс → пн–вс). Пример: 1–7 пн–вс → ближайший пн–вс около того же числа прошлого месяца.
 */
const computePreviousMonth = (fromStr, toStr) => {
  const fromDate = parseLocalYmd(fromStr);
  const toDate = parseLocalYmd(toStr);
  const lengthDays = dayDiffInclusive(fromDate, toDate);

  let prevStart = new Date(fromDate.getFullYear(), fromDate.getMonth() - 1, fromDate.getDate());
  // Если день «перескочил» месяц (31 → 2/3), откатываем к последнему дню целевого месяца
  if (prevStart.getMonth() === fromDate.getMonth()) {
    prevStart = new Date(fromDate.getFullYear(), fromDate.getMonth(), 0);
  }

  const targetDow = fromDate.getDay();
  const forward = (targetDow - prevStart.getDay() + 7) % 7;
  const backward = (prevStart.getDay() - targetDow + 7) % 7;
  if (backward > 0 && backward < forward) {
    prevStart.setDate(prevStart.getDate() - backward);
  } else if (forward > 0) {
    prevStart.setDate(prevStart.getDate() + forward);
  }

  const prevEnd = new Date(prevStart);
  prevEnd.setDate(prevEnd.getDate() + lengthDays);
  return { from: formatDateInput(prevStart), to: formatDateInput(prevEnd) };
};

const applyComparePreset = (preset, fromStr, toStr) => {
  if (!fromStr || !toStr) return null;
  if (preset === 'prev_week') return computePreviousWeek(fromStr, toStr);
  if (preset === 'prev_month') return computePreviousMonth(fromStr, toStr);
  return computePreviousPeriod(fromStr, toStr);
};

const COMPARE_PRESET_OPTIONS = [
  { key: 'previous', label: 'Предыдущий период' },
  { key: 'prev_week', label: 'Прошлая неделя' },
  { key: 'prev_month', label: 'Предыдущий месяц' },
];

// Компонент календаря для выбора диапазона дат
const DateRangeCalendar = ({ 
  month, 
  onMonthChange, 
  periodFrom, 
  periodTo, 
  compareFrom, 
  compareTo, 
  showCompare,
  onDateClick,
  selectingPeriod,
}) => {
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const weekDays = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Понедельник = 0
    
    const days = [];
    // Пустые ячейки в начале месяца
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Дни месяца
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };
  
  const isDateInRange = (date, from, to) => {
    if (!date || !from || !to) return false;
    const dateStr = formatDateInput(date);
    return dateStr >= from && dateStr <= to;
  };
  
  const isDateStart = (date, from) => {
    if (!date || !from) return false;
    return formatDateInput(date) === from;
  };
  
  const isDateEnd = (date, to) => {
    if (!date || !to) return false;
    return formatDateInput(date) === to;
  };
  
  const isDateInCompareRange = (date) => {
    if (!showCompare || !compareFrom || !compareTo) return false;
    return isDateInRange(date, compareFrom, compareTo);
  };
  
  const getDateClass = (date) => {
    if (!date) return '';
    
    const dateStr = formatDateInput(date);
    const today = formatDateInput(new Date());
    const isToday = dateStr === today;
    
    const isMainStart = isDateStart(date, periodFrom);
    const isMainEnd = isDateEnd(date, periodTo);
    const isInMainRange = periodFrom && periodTo && isDateInRange(date, periodFrom, periodTo);
    const isCompareStart = showCompare && isDateStart(date, compareFrom);
    const isCompareEnd = showCompare && isDateEnd(date, compareTo);
    const isInCompareRange = isDateInCompareRange(date);
    
    let classes = 'w-10 h-10 flex items-center justify-center text-sm rounded transition-colors cursor-pointer ';
    
    if (isToday) {
      classes += 'font-semibold ';
    }
    
    if (isMainStart || isMainEnd) {
      classes += 'bg-blue-600 text-white rounded-full z-10 relative ';
    } else if (isCompareStart || isCompareEnd) {
      classes += 'bg-slate-500 text-white rounded-full z-10 relative ';
    } else if (isInMainRange) {
      classes += 'bg-blue-100 text-blue-900 ';
    } else if (isInCompareRange) {
      classes += 'bg-gray-200 text-gray-700 ';
    } else {
      classes += 'hover:bg-gray-100 text-gray-700 ';
    }
    
    return classes;
  };
  
  const currentMonthDays = getDaysInMonth(month);
  const nextMonth = new Date(month);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const nextMonthDays = getDaysInMonth(nextMonth);
  
  const prevMonth = () => {
    const newMonth = new Date(month);
    newMonth.setMonth(newMonth.getMonth() - 1);
    onMonthChange(newMonth);
  };
  
  const nextMonthNav = () => {
    const newMonth = new Date(month);
    newMonth.setMonth(newMonth.getMonth() + 1);
    onMonthChange(newMonth);
  };

  const selectionHint = !showCompare
    ? (periodFrom && !periodTo
      ? 'Основной период: выберите дату окончания'
      : 'Основной период: выберите дату начала')
    : selectingPeriod === 'main'
      ? (periodFrom && !periodTo
        ? 'Шаг 1 · Основной период: выберите дату окончания'
        : 'Шаг 1 · Основной период: выберите дату начала')
      : (compareFrom && !compareTo
        ? 'Шаг 2 · Период сравнения: выберите дату окончания'
        : 'Шаг 2 · Период сравнения: выберите дату начала');
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
        <p className="text-sm text-gray-700">{selectionHint}</p>
        {showCompare && (
          <div className="flex items-center gap-2 text-xs shrink-0">
            <span className={`px-2 py-0.5 rounded-full ${selectingPeriod === 'main' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'}`}>
              Основной
            </span>
            <span className="text-gray-400">→</span>
            <span className={`px-2 py-0.5 rounded-full ${selectingPeriod === 'compare' ? 'bg-slate-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
              Сравнение
            </span>
          </div>
        )}
      </div>
      
      {/* Два календаря */}
      <div className="grid grid-cols-2 gap-6">
        {/* Первый календарь */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={prevMonth}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <HiChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h4 className="text-sm font-semibold text-gray-900">
              {months[month.getMonth()]} {month.getFullYear()}
            </h4>
            <div className="w-6"></div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-xs text-gray-500 font-medium text-center py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {currentMonthDays.map((date, idx) => (
              <div
                key={idx}
                onClick={() => date && onDateClick(date)}
                className={getDateClass(date)}
              >
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
        </div>
        
        {/* Второй календарь */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="w-6"></div>
            <h4 className="text-sm font-semibold text-gray-900">
              {months[nextMonth.getMonth()]} {nextMonth.getFullYear()}
            </h4>
            <button
              onClick={nextMonthNav}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <HiChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-xs text-gray-500 font-medium text-center py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {nextMonthDays.map((date, idx) => (
              <div
                key={idx}
                onClick={() => date && onDateClick(date)}
                className={getDateClass(date)}
              >
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Легенда */}
      <div className="flex items-center gap-6 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
          <span>Начало/конец основного</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100"></div>
          <span>Основной период</span>
        </div>
        {showCompare && (
          <>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-500 rounded-full"></div>
              <span>Начало/конец сравнения</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200"></div>
              <span>Период сравнения</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function CampaignStatistics({ clientId }) {
  const { notify } = useNotification();
  const initialRange = defaultRange();
  const [periodFrom, setPeriodFrom] = useState(initialRange.from);
  const [periodTo, setPeriodTo] = useState(initialRange.to);
  const [compareFrom, setCompareFrom] = useState('');
  const [compareTo, setCompareTo] = useState('');
  const [compareDirty, setCompareDirty] = useState(false);
  const [comparePreset, setComparePreset] = useState('previous'); // previous | prev_week | prev_month | manual
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [groupBy, setGroupBy] = useState('day');
  const [selectedMetrics, setSelectedMetrics] = useState(['spend', 'orders']);
  const [showCompareLines, setShowCompareLines] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set()); // Выбранные кампании для графика
  
  // Состояния для календаря
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingPeriod, setSelectingPeriod] = useState('main'); // 'main' или 'compare'
  // Временные состояния для модального окна (применяются только после нажатия "Применить")
  const [tempPeriodFrom, setTempPeriodFrom] = useState(initialRange.from);
  const [tempPeriodTo, setTempPeriodTo] = useState(initialRange.to);
  const [tempCompareFrom, setTempCompareFrom] = useState('');
  const [tempCompareTo, setTempCompareTo] = useState('');
  const [tempCompareDirty, setTempCompareDirty] = useState(false);
  const [tempComparePreset, setTempComparePreset] = useState('previous');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Биддер: список кампаний с API /campaigns для статуса, стратегии, расписания
  const [campaignsList, setCampaignsList] = useState([]);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [heatmapModalOpen, setHeatmapModalOpen] = useState(false);
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [automationEnabled, setAutomationEnabled] = useState(true);
  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const [selectedCampaignForStrategy, setSelectedCampaignForStrategy] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedCampaignForSchedule, setSelectedCampaignForSchedule] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [tableGroupOrder, setTableGroupOrder] = useState(['campaign', 'placement', 'skleyka', 'norm_query']);
  const [dragGroupingKey, setDragGroupingKey] = useState(null);
  const [groupingModalOpen, setGroupingModalOpen] = useState(false);
  const [createCampaignModalOpen, setCreateCampaignModalOpen] = useState(false);
  const [tableMetricsModalOpen, setTableMetricsModalOpen] = useState(false);
  const [visibleStatusBuckets, setVisibleStatusBuckets] = useState(['active', 'paused', 'completed']);
  const [openColumnFilter, setOpenColumnFilter] = useState(null);
  const [campaignFilterIds, setCampaignFilterIds] = useState(null);
  const [campaignFilterDraft, setCampaignFilterDraft] = useState('');
  const [campaignFilterApplied, setCampaignFilterApplied] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState(null);
  const [visibleTableMetrics, setVisibleTableMetrics] = useState(tableMetricOptions.map((m) => m.key));
  const [expandedGroupNodes, setExpandedGroupNodes] = useState(new Set());
  const [campaignDetailsMap, setCampaignDetailsMap] = useState({});
  const [loadingCampaignDetails, setLoadingCampaignDetails] = useState({});
  const [nmToSkleykaMap, setNmToSkleykaMap] = useState({});
  const [advertArticlesMap, setAdvertArticlesMap] = useState(() => new Map());
  const [nmVendorMap, setNmVendorMap] = useState(() => new Map());
  const [vendorToNmMap, setVendorToNmMap] = useState(() => new Map());

  // Метка последнего известного обновления данных на бэкенде (скрипты пишут её после выгрузки)
  const lastKnownDataUpdateAtRef = useRef(null);

  // Автоматически вычисляем период сравнения по выбранному пресету
  useEffect(() => {
    if (compareDirty || comparePreset === 'manual') return;
    if (!periodFrom || !periodTo) return;
    const range = applyComparePreset(comparePreset, periodFrom, periodTo);
    if (!range) return;
    setCompareFrom(range.from);
    setCompareTo(range.to);
  }, [periodFrom, periodTo, compareDirty, comparePreset]);

  // Инициализация временных значений при открытии модального окна
  useEffect(() => {
    if (dateModalOpen) {
      setTempPeriodFrom(periodFrom);
      setTempPeriodTo(periodTo);
      setTempCompareFrom(compareFrom);
      setTempCompareTo(compareTo);
      setTempCompareDirty(compareDirty);
      setTempComparePreset(comparePreset);
      setSelectingPeriod('main');
    }
  }, [dateModalOpen]);

  // Автоматически вычисляем период сравнения для временных значений
  useEffect(() => {
    if (!dateModalOpen || tempCompareDirty || tempComparePreset === 'manual') return;
    if (!tempPeriodFrom || !tempPeriodTo) return;
    const range = applyComparePreset(tempComparePreset, tempPeriodFrom, tempPeriodTo);
    if (!range) return;
    setTempCompareFrom(range.from);
    setTempCompareTo(range.to);
  }, [tempPeriodFrom, tempPeriodTo, tempCompareDirty, tempComparePreset, dateModalOpen]);

  const handleDateApply = () => {
    setPeriodFrom(tempPeriodFrom);
    setPeriodTo(tempPeriodTo);
    setCompareFrom(tempCompareFrom);
    setCompareTo(tempCompareTo);
    setCompareDirty(tempCompareDirty);
    setComparePreset(tempComparePreset);
    setDateModalOpen(false);
  };

  /** Активный пресет сравнения (если был manual — возвращаемся к «Предыдущий период») */
  const resolveComparePreset = (preset = tempComparePreset) =>
    (preset === 'manual' ? 'previous' : preset);

  /** Выбрать основной период и сразу применить текущий пресет сравнения */
  const applyTempMainPeriod = (fromStr, toStr) => {
    const preset = resolveComparePreset();
    setTempPeriodFrom(fromStr);
    setTempPeriodTo(toStr);
    setTempComparePreset(preset);
    setTempCompareDirty(false);
    setSelectingPeriod('main');
    const range = applyComparePreset(preset, fromStr, toStr);
    if (range) {
      setTempCompareFrom(range.from);
      setTempCompareTo(range.to);
    }
  };

  const applyTempComparePreset = (preset) => {
    if (!tempPeriodFrom || !tempPeriodTo) return;
    const range = applyComparePreset(preset, tempPeriodFrom, tempPeriodTo);
    if (!range) return;
    setTempComparePreset(preset);
    setTempCompareFrom(range.from);
    setTempCompareTo(range.to);
    setTempCompareDirty(false);
    // Не уводим в режим сравнения — календарь снова может выбрать основной период
    setSelectingPeriod('main');
  };

  const handleCalendarDateClick = (date) => {
    const dateStr = formatDateInput(date);
    const mainComplete = Boolean(tempPeriodFrom && tempPeriodTo);
    const compareComplete = Boolean(tempCompareFrom && tempCompareTo);

    // Ручной выбор сравнения — только пока основной готов, а сравнение ещё не завершено
    const pickingCompare =
      showCompareLines
      && selectingPeriod === 'compare'
      && mainComplete
      && !compareComplete;

    if (!pickingCompare) {
      // Основной период (новый или продолжение)
      if (!tempPeriodFrom || mainComplete) {
        setTempPeriodFrom(dateStr);
        setTempPeriodTo('');
        setSelectingPeriod('main');
        return;
      }

      let fromStr = tempPeriodFrom;
      let toStr = dateStr;
      if (dateStr < tempPeriodFrom) {
        fromStr = dateStr;
        toStr = tempPeriodFrom;
        setTempPeriodFrom(fromStr);
      }
      setTempPeriodTo(toStr);

      const preset = resolveComparePreset();
      setTempComparePreset(preset);
      setTempCompareDirty(false);
      if (showCompareLines) {
        const range = applyComparePreset(preset, fromStr, toStr);
        if (range) {
          setTempCompareFrom(range.from);
          setTempCompareTo(range.to);
        }
        setSelectingPeriod('compare');
      }
      return;
    }

    // Период сравнения вручную
    if (!tempCompareFrom || compareComplete) {
      setTempCompareFrom(dateStr);
      setTempCompareTo('');
      setTempCompareDirty(true);
      setTempComparePreset('manual');
      return;
    }
    if (dateStr >= tempCompareFrom) {
      setTempCompareTo(dateStr);
    } else {
      setTempCompareTo(tempCompareFrom);
      setTempCompareFrom(dateStr);
    }
    setTempCompareDirty(true);
    setTempComparePreset('manual');
    setSelectingPeriod('main');
  };

  const fetchSummary = useCallback(async (showLoading = true) => {
    if (showLoading) {
    setLoading(true);
    }
    setError('');
    try {
      const params = {
        client_id: clientId,
        from: periodFrom,
        to: periodTo,
        group_by: groupBy,
      };
      if (compareFrom && compareTo) {
        params.compare_from = compareFrom;
        params.compare_to = compareTo;
      }
      // График по выбранным кампаниям: передаём advert_ids
      if (selectedCampaigns && selectedCampaigns.size > 0) {
        params.advert_ids = Array.from(selectedCampaigns).join(',');
      }
      const { data } = await apiClient.get('/stats/summary', { params });
      setStats(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Не удалось загрузить статистику');
    } finally {
      if (showLoading) {
      setLoading(false);
    }
    }
  }, [clientId, periodFrom, periodTo, compareFrom, compareTo, groupBy, selectedCampaigns]);

  // Загрузка данных при изменении параметров
  useEffect(() => {
    if (clientId) {
      fetchSummary();
    }
  }, [clientId, fetchSummary]);

  const loadCampaignsList = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await apiClient.get('/campaigns', { params: { client_id: clientId } });
      setCampaignsList(data.campaigns || []);
    } catch (err) {
      console.error('Ошибка загрузки списка кампаний:', err);
    }
  }, [clientId]);

  const loadBudgetInfo = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await apiClient.get(`/budget/${clientId}`);
      setBudgetInfo(data);
    } catch (err) {
      console.error('Ошибка загрузки бюджета:', err);
    }
  }, [clientId]);

  const refreshBidderDataInBackground = useCallback(() => {
    loadCampaignsList();
    loadBudgetInfo();
  }, [loadCampaignsList, loadBudgetInfo]);

  const saveCampaignStrategy = useCallback(async (settings) => {
    const params = clientId ? { client_id: clientId } : {};
    await apiClient.put(`/bidder/campaign/${settings.campaign_id}/settings`, settings, { params });
    setCampaignsList((prev) => prev.map((c) => patchCampaignWithStrategySettings(c, settings)));
    refreshBidderDataInBackground();
  }, [clientId, refreshBidderDataInBackground]);

  useEffect(() => {
    if (clientId) {
      loadCampaignsList();
      loadBudgetInfo();
    }
  }, [clientId, loadCampaignsList, loadBudgetInfo]);

  useEffect(() => {
    setAutomationEnabled(budgetInfo?.budget?.automation_enabled !== false);
  }, [budgetInfo]);

  const budgetMonth = useMemo(() => {
    if (budgetInfo?.month) return budgetInfo.month;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, [budgetInfo?.month]);

  useEffect(() => {
    const loadSkleykiMap = async () => {
      if (!clientId) return;
      try {
        const { data } = await apiClient.get('/skleyki/tree', { params: { client_id: clientId } });
        const map = {};
        const vendorByNm = new Map();
        const nmByVendor = new Map();
        const articlesByAdvert = new Map();
        const ensureAdvert = (advertId) => {
          const id = Number(advertId);
          if (!Number.isFinite(id) || id <= 0) return null;
          if (!articlesByAdvert.has(id)) {
            articlesByAdvert.set(id, { nmIds: new Set(), vendorCodes: new Set() });
          }
          return articlesByAdvert.get(id);
        };
        (data?.groups || []).forEach((group) => {
          (group.cards || []).forEach((card) => {
            const nmId = Number(card?.nm_id);
            const vendor = String(card?.vendor_code || '').trim();
            const vendorKey = vendor.toLowerCase();
            if (nmId) {
              map[nmId] = `Склейка ${group.imt_id}`;
              if (vendorKey) {
                vendorByNm.set(nmId, vendorKey);
                if (!nmByVendor.has(vendorKey)) nmByVendor.set(vendorKey, nmId);
              }
            }
            (card.campaigns || []).forEach((camp) => {
              const entry = ensureAdvert(camp?.advert_id);
              if (!entry) return;
              if (nmId) entry.nmIds.add(nmId);
              if (vendorKey) entry.vendorCodes.add(vendorKey);
            });
          });
        });
        for (const entry of articlesByAdvert.values()) {
          for (const nmId of entry.nmIds) {
            const v = vendorByNm.get(Number(nmId));
            if (v) entry.vendorCodes.add(v);
          }
        }
        setNmToSkleykaMap(map);
        setAdvertArticlesMap(articlesByAdvert);
        setNmVendorMap(vendorByNm);
        setVendorToNmMap(nmByVendor);
      } catch (e) {
        setNmToSkleykaMap({});
        setAdvertArticlesMap(new Map());
        setNmVendorMap(new Map());
        setVendorToNmMap(new Map());
      }
    };
    loadSkleykiMap();
  }, [clientId]);

  // Опрос метки обновления данных: скрипты выгрузки/агрегации пишут её после отработки — тогда подтягиваем данные
  useEffect(() => {
    if (!clientId) return;

    const pollDataUpdated = async () => {
      try {
        const { data } = await apiClient.get('/stats/data-updated-at');
        const updatedAt = data?.updated_at || null;
        if (!updatedAt) return;
        // Рефетч при первой метке или при появлении новой (после отработки скриптов)
        if (lastKnownDataUpdateAtRef.current !== updatedAt) {
          await fetchSummary(false);
          await loadCampaignsList();
          await loadBudgetInfo();
        }
        lastKnownDataUpdateAtRef.current = updatedAt;
      } catch (_) {}
    };

    const interval = setInterval(pollDataUpdated, 60 * 1000); // каждые 60 сек
    pollDataUpdated(); // первый раз сразу

    return () => clearInterval(interval);
  }, [clientId, fetchSummary, loadCampaignsList, loadBudgetInfo]);

  const getChartMetricValue = (row, metric) => {
    if (!row) return null;
    const views = Number(row.views ?? row.impressions) || 0;
    const clicks = Number(row.clicks) || 0;
    const spend = Number(row.spend) || 0;
    const orders = Number(row.orders) || 0;
    const atbs = Number(row.atbs) || 0;
    const revenue = Number(row.revenue) || 0;

    switch (metric) {
      case 'views':
        return views;
      case 'revenue':
        return revenue;
      case 'ctr':
        return row.ctr != null ? Number(row.ctr) : (views > 0 ? (clicks / views) * 100 : 0);
      case 'cpc':
        return row.cpc != null ? Number(row.cpc) : (clicks > 0 ? spend / clicks : 0);
      case 'cpo':
        return row.cpo != null ? Number(row.cpo) : (orders > 0 ? spend / orders : null);
      case 'cart_cost':
        return row.cart_cost != null ? Number(row.cart_cost) : (atbs > 0 ? spend / atbs : null);
      case 'drr':
        return revenue > 0 ? (spend / revenue) * 100 : 0;
      case 'avg_position':
        return row.avg_position != null ? Number(row.avg_position) : null;
      default:
        return row[metric] != null ? Number(row[metric]) : 0;
    }
  };

  const chartData = useMemo(() => {
    if (!stats?.timeSeries?.period?.length && !stats?.timeSeries?.compare?.length) {
      return [];
    }

    const primarySeries =
      stats.timeSeries.period.length > 0 ? stats.timeSeries.period : stats.timeSeries.compare ?? [];
    const secondarySeries = stats.timeSeries.compare ?? [];
    return primarySeries.map((row, index) => {
      const compareRow = secondarySeries[index];
      const dataset = { label: row.label };
      selectedMetrics.forEach((metric) => {
        const periodVal = getChartMetricValue(row, metric);
        const compareVal = compareRow ? getChartMetricValue(compareRow, metric) : null;
        dataset[`period_${metric}`] = periodVal != null ? Number(periodVal) : 0;
        dataset[`compare_${metric}`] = compareVal != null ? Number(compareVal) : null;
      });
      return dataset;
    });
  }, [stats, selectedMetrics]);

  const handleMetricToggle = (metric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metric) ? prev.filter((item) => item !== metric) : [...prev, metric]
    );
  };

  const summaryCards = useMemo(() => {
    if (!stats?.period?.totals) return [];

    const totals = stats.period.totals;
    const compareTotals = stats.compare?.totals;
    
    // Расчет ДРР (Расход / Доход * 100)
    const drr = totals.revenue > 0 ? (totals.spend / totals.revenue) * 100 : 0;
    const compareDrr = compareTotals?.revenue > 0 ? (compareTotals?.spend / compareTotals?.revenue) * 100 : 0;

    // CTR (клики / показы * 100) — берём из totals или считаем
    const viewsVal = totals.views ?? totals.impressions ?? 0;
    const ctr = totals.ctr != null ? Number(totals.ctr) : (viewsVal > 0 ? (totals.clicks / viewsVal) * 100 : 0);
    const compareViews = compareTotals?.views ?? compareTotals?.impressions ?? 0;
    const compareCtr = compareTotals
      ? (compareTotals.ctr != null ? Number(compareTotals.ctr) : (compareViews > 0 ? (compareTotals.clicks / compareViews) * 100 : 0))
      : undefined;

    const intFmt = (v) => (v != null ? Number(v).toLocaleString('ru-RU') : '0');

    return [
      {
        label: 'Расход',
        key: 'spend',
        value: totals.spend,
        compare: compareTotals?.spend,
        format: (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 }),
      },
      {
        label: 'Заказы',
        key: 'orders',
        value: totals.orders,
        compare: compareTotals?.orders,
        format: (v) => v?.toLocaleString('ru-RU') ?? '0',
      },
      {
        label: 'Доход',
        key: 'revenue',
        value: totals.revenue || 0,
        compare: compareTotals?.revenue,
        format: (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 }),
      },
      {
        label: 'CPO',
        key: 'cpo',
        value: totals.cpo || 0,
        compare: compareTotals?.cpo,
        format: (v) => v !== null && v !== undefined ? `${v.toFixed(2)} ₽` : '—',
      },
      {
        label: 'ДРР',
        key: 'drr',
        value: drr,
        compare: compareTotals ? compareDrr : undefined,
        format: (v) => `${v.toFixed(2)}%`,
      },
      {
        label: 'Показы',
        key: 'views',
        value: viewsVal,
        compare: compareTotals ? compareViews : undefined,
        format: intFmt,
      },
      {
        label: 'Клики',
        key: 'clicks',
        value: totals.clicks || 0,
        compare: compareTotals?.clicks,
        format: intFmt,
      },
      {
        label: 'Корзины',
        key: 'atbs',
        value: totals.atbs || 0,
        compare: compareTotals?.atbs,
        format: intFmt,
      },
      {
        label: 'CTR',
        key: 'ctr',
        value: ctr,
        compare: compareCtr,
        format: (v) => `${Number(v).toFixed(2)}%`,
      },
    ];
  }, [stats]);

  // Данные для круговой диаграммы бюджета (потрачено / прогноз / остаток)
  const budgetOverview = useMemo(() => {
    const b = budgetInfo?.budget;
    const total = Number(b?.total_budget) || 0;
    const spent = Number(b?.spent_budget) || 0;
    const remaining = total - spent;
    const usedPct = total > 0 ? Math.min((spent / total) * 100, 100) : 0;

    const monthKey = budgetInfo?.month || (() => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    })();

    // Сумма дневных лимитов запущенных кампаний — то, на что опирается распределение
    const dailyCapSum = Array.isArray(budgetInfo?.campaigns)
      ? budgetInfo.campaigns.reduce((sum, c) => {
          if (Number(c.status) !== 9) return sum;
          const lim = Number(c.daily_limit);
          return lim > 0 ? sum + lim : sum;
        }, 0)
      : null;

    const forecastInfo = spent > 0
      ? calcMonthlyBudgetForecast({
          spent,
          monthKey,
          dailyCapSum: dailyCapSum != null ? dailyCapSum : null,
          totalBudget: total || null,
        })
      : null;

    const forecast = forecastInfo?.forecast ?? null;
    const paceForecast = forecastInfo?.paceForecast ?? null;
    const forecastExtra = forecast != null ? Math.max(0, forecast - spent) : 0;
    const remainingAfterForecast = forecast != null ? Math.max(0, total - forecast) : Math.max(0, remaining);
    const forecastOvershoot = forecast != null ? Math.max(0, forecast - total) : 0;
    const forecastPct = total > 0 && forecast != null ? (forecast / total) * 100 : null;
    const paceOvershoot = paceForecast != null ? Math.max(0, paceForecast - total) : 0;

    const pieData = [];
    if (forecast != null && forecastExtra > 0.01) {
      pieData.push({ name: 'Потрачено', value: Math.max(0, spent) });
      const forecastWithinBudget = Math.max(0, Math.min(forecast - spent, total - spent));
      if (forecastWithinBudget > 0.01) {
        pieData.push({ name: 'Прогноз', value: forecastWithinBudget });
      }
      if (remainingAfterForecast > 0.01) {
        pieData.push({ name: 'Остаток', value: remainingAfterForecast });
      }
      if (forecastOvershoot > 0.01) {
        pieData.push({ name: 'Превышение', value: forecastOvershoot });
      }
    } else {
      pieData.push({ name: 'Потрачено', value: Math.max(0, spent) });
      pieData.push({ name: 'Остаток', value: Math.max(0, remaining) });
    }

    return {
      hasBudget: !!b,
      total,
      spent,
      remaining,
      usedPct,
      forecast,
      paceForecast,
      paceOvershoot,
      forecastMode: forecastInfo?.mode || null,
      dailyCapSum,
      forecastExtra,
      remainingAfterForecast,
      forecastOvershoot,
      forecastPct,
      pieData,
      hasForecast: forecast != null && forecastExtra > 0.01,
    };
  }, [budgetInfo]);

  const [sortColumn, setSortColumn] = useState(null); // 'impressions', 'clicks', 'spend', etc.
  const [sortDirection, setSortDirection] = useState(null); // 'desc', 'asc', null

  const handleSort = (column) => {
    if (sortColumn !== column) {
      // Первый клик - сортировка по убыванию
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      // Второй клик - сортировка по возрастанию
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      // Третий клик - убрать сортировку
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  const uniqueCampaigns = useMemo(() => {
    const seen = new Map();
    (stats?.campaigns ?? []).forEach((row) => {
      const advertId = Number(row.advert_id);
      if (!Number.isFinite(advertId)) return;
      if (!seen.has(advertId)) seen.set(advertId, row);
    });
    return [...seen.values()];
  }, [stats?.campaigns]);

  const sortedCampaignRows = useMemo(() => {
    if (!sortColumn || !sortDirection) return uniqueCampaigns;
    
    const rows = [...uniqueCampaigns];
    return rows.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'impressions':
          aValue = Number(a.metrics.impressions ?? a.metrics.views) || 0;
          bValue = Number(b.metrics.impressions ?? b.metrics.views) || 0;
          break;
        case 'clicks':
          aValue = a.metrics.clicks || 0;
          bValue = b.metrics.clicks || 0;
          break;
        case 'spend':
          aValue = a.metrics.spend || 0;
          bValue = b.metrics.spend || 0;
          break;
        case 'orders':
          aValue = a.metrics.orders || 0;
          bValue = b.metrics.orders || 0;
          break;
        case 'direct_orders':
          aValue = a.metrics.direct_orders || 0;
          bValue = b.metrics.direct_orders || 0;
          break;
        case 'associated_orders':
          aValue = a.metrics.associated_orders || 0;
          bValue = b.metrics.associated_orders || 0;
          break;
        case 'direct_revenue':
          aValue = a.metrics.direct_revenue || 0;
          bValue = b.metrics.direct_revenue || 0;
          break;
        case 'associated_revenue':
          aValue = a.metrics.associated_revenue || 0;
          bValue = b.metrics.associated_revenue || 0;
          break;
        case 'revenue':
          aValue = a.metrics.revenue || 0;
          bValue = b.metrics.revenue || 0;
          break;
        case 'atbs':
          aValue = a.metrics.atbs || 0;
          bValue = b.metrics.atbs || 0;
          break;
        case 'ctr':
          aValue = a.metrics.ctr || 0;
          bValue = b.metrics.ctr || 0;
          break;
        case 'cpc':
          aValue = a.metrics.cpc || 0;
          bValue = b.metrics.cpc || 0;
          break;
        case 'cpo':
          aValue = a.metrics.cpo || 0;
          bValue = b.metrics.cpo || 0;
          break;
        case 'cart_cost':
          aValue = a.metrics.cart_cost || 0;
          bValue = b.metrics.cart_cost || 0;
          break;
        case 'avg_position':
          aValue = a.metrics.avg_position != null ? a.metrics.avg_position : Infinity;
          bValue = b.metrics.avg_position != null ? b.metrics.avg_position : Infinity;
          break;
        case 'drr':
          aValue = a.metrics.revenue > 0 ? (a.metrics.spend / a.metrics.revenue) * 100 : 0;
          bValue = b.metrics.revenue > 0 ? (b.metrics.spend / b.metrics.revenue) * 100 : 0;
          break;
        case 'promoted_stock_remain':
          aValue = a.promoted_stock_remain != null ? Number(a.promoted_stock_remain) : -1;
          bValue = b.promoted_stock_remain != null ? Number(b.promoted_stock_remain) : -1;
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'desc') {
        return bValue - aValue;
      } else {
        return aValue - bValue;
      }
    });
  }, [uniqueCampaigns, sortColumn, sortDirection]);

  const campaignsListMap = useMemo(() => {
    const m = new Map();
    (campaignsList || []).forEach((c) => m.set(c.advertId, c));
    return m;
  }, [campaignsList]);

  const mergedCampaignRows = useMemo(() => {
    return (sortedCampaignRows || []).map((c) => {
      const extra = campaignsListMap.get(c.advert_id) || {};
      // Статистика периода только из /stats/summary (metrics), не из /campaigns (90 дней)
      const { orders: _o, spend: _s, views: _v, clicks: _cl, revenue: _r, ...extraSafe } = extra;
      return { ...extraSafe, ...c, metrics: c.metrics, advertId: c.advert_id };
    });
  }, [sortedCampaignRows, campaignsListMap]);

  const campaignRows = mergedCampaignRows;

  const getStatusBucket = (status) => {
    const n = Number(status);
    if (n === 9) return 'active';
    if (n === 11) return 'paused';
    if (n === 4 || n === 7 || n === -1) return 'completed';
    return 'active';
  };

  const toggleStatusBucket = (bucket) => {
    setVisibleStatusBuckets((prev) => {
      if (prev.includes(bucket)) {
        if (prev.length === 1) return prev;
        return prev.filter((b) => b !== bucket);
      }
      return [...prev, bucket];
    });
  };

  useEffect(() => {
    if (!openColumnFilter || openColumnFilter === 'campaign') return undefined;
    const close = () => setOpenColumnFilter(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openColumnFilter]);

  const campaignFilterOptions = useMemo(
    () => campaignRows
      .map((c) => {
        const advertId = Number(c.advert_id);
        const fromMap = advertArticlesMap.get(advertId);
        const nmIds = new Set(fromMap?.nmIds || []);
        const vendorCodes = new Set(fromMap?.vendorCodes || []);
        const promotedNm = Number(c.promoted_nm_id);
        if (Number.isFinite(promotedNm) && promotedNm > 0) {
          nmIds.add(promotedNm);
          const v = nmVendorMap.get(promotedNm);
          if (v) vendorCodes.add(v);
        }
        return {
          id: advertId,
          name: c.name || `Кампания ${advertId}`,
          promoted_nm_id: Number.isFinite(promotedNm) && promotedNm > 0 ? promotedNm : null,
          nmIds: [...nmIds],
          vendorCodes: [...vendorCodes],
        };
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru')),
    [campaignRows, advertArticlesMap, nmVendorMap],
  );

  const campaignFilterTokens = useMemo(
    () => parseCampaignFilterTokens(campaignFilterApplied),
    [campaignFilterApplied],
  );

  const campaignFilterDraftTokens = useMemo(
    () => parseCampaignFilterTokens(campaignFilterDraft),
    [campaignFilterDraft],
  );

  const typeFilterOptions = useMemo(
    () => [...new Set(campaignRows.map((c) => c.typeLabel || '—'))].sort((a, b) => a.localeCompare(b, 'ru')),
    [campaignRows],
  );

  const paymentFilterOptions = useMemo(
    () => [...new Set(campaignRows.map((c) => c.paymentLabel || '—'))].sort((a, b) => a.localeCompare(b, 'ru')),
    [campaignRows],
  );

  const isCampaignFilterActive = campaignFilterIds !== null || campaignFilterTokens.length > 0;
  const isTypeFilterActive = typeFilter !== null;
  const isPaymentFilterActive = paymentFilter !== null;
  const isStatusFilterActive = visibleStatusBuckets.length < 3;

  const isCampaignFilterChecked = (id) => (
    campaignFilterIds === null || campaignFilterIds.has(id)
  );

  const isLabelFilterChecked = (filterSet, allOptions, value) => (
    filterSet === null || filterSet.has(value)
  );

  const toggleCampaignFilterId = (id) => {
    const allIds = campaignFilterOptions.map((c) => c.id);
    const selected = campaignFilterIds === null ? new Set(allIds) : new Set(campaignFilterIds);
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    if (selected.size === allIds.length) setCampaignFilterIds(null);
    else setCampaignFilterIds(selected);
  };

  const toggleLabelFilter = (setter, allOptions, current, value) => {
    const selected = current === null ? new Set(allOptions) : new Set(current);
    if (selected.has(value)) selected.delete(value);
    else selected.add(value);
    if (selected.size === allOptions.length) setter(null);
    else setter(selected);
  };

  const resetCampaignFilter = () => {
    setCampaignFilterIds(null);
    setCampaignFilterDraft('');
    setCampaignFilterApplied('');
  };

  const applyCampaignTextFilter = useCallback((draftText = campaignFilterDraft, { close = true } = {}) => {
    const text = String(draftText ?? '');
    const tokens = parseCampaignFilterTokens(text);
    setCampaignFilterDraft(text);
    setCampaignFilterApplied(text);
    if (!tokens.length) {
      setCampaignFilterIds(null);
    } else {
      const matchedIds = campaignFilterOptions
        .filter((c) =>
          tokens.some((token) =>
            campaignMatchesFilterToken(
              c,
              token,
              { nmIds: c.nmIds, vendorCodes: c.vendorCodes },
              vendorToNmMap,
            ),
          ),
        )
        .map((c) => Number(c.id))
        .filter((id) => Number.isFinite(id) && id > 0);
      setCampaignFilterIds(new Set(matchedIds));
    }
    if (close) setOpenColumnFilter(null);
  }, [campaignFilterDraft, campaignFilterOptions, vendorToNmMap]);

  // Закрытие фильтра кампаний кликом снаружи — тоже применяет список
  useEffect(() => {
    if (openColumnFilter !== 'campaign') return undefined;
    const close = () => {
      applyCampaignTextFilter(campaignFilterDraft, { close: false });
      setOpenColumnFilter(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openColumnFilter, campaignFilterDraft, applyCampaignTextFilter]);

  const filteredCampaignFilterOptions = useMemo(() => {
    const tokens = campaignFilterDraftTokens.length
      ? campaignFilterDraftTokens
      : campaignFilterTokens;
    if (!tokens.length) return campaignFilterOptions;
    return campaignFilterOptions.filter((c) =>
      tokens.some((token) =>
        campaignMatchesFilterToken(
          c,
          token,
          { nmIds: c.nmIds, vendorCodes: c.vendorCodes },
          vendorToNmMap,
        ),
      ),
    );
  }, [campaignFilterOptions, campaignFilterDraftTokens, campaignFilterTokens, vendorToNmMap]);

  /** ID кампаний, подходящих под применённый текстовый фильтр (источник истины для таблицы). */
  const campaignTextFilterIdSet = useMemo(() => {
    if (!campaignFilterTokens.length) return null;
    const ids = new Set();
    for (const c of campaignFilterOptions) {
      if (
        campaignFilterTokens.some((token) =>
          campaignMatchesFilterToken(
            c,
            token,
            { nmIds: c.nmIds, vendorCodes: c.vendorCodes },
            vendorToNmMap,
          ),
        )
      ) {
        ids.add(Number(c.id));
      }
    }
    return ids;
  }, [campaignFilterTokens, campaignFilterOptions, vendorToNmMap]);

  const parseAvgPosition = (value) => {
    if (value == null || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const filteredCampaignRows = useMemo(
    () => campaignRows.filter((c) => {
      const advertId = Number(c.advert_id);
      if (!visibleStatusBuckets.includes(getStatusBucket(c.status))) return false;
      // Текстовый фильтр (список артикулов/названий) — главный
      if (campaignTextFilterIdSet !== null && !campaignTextFilterIdSet.has(advertId)) return false;
      // Чекбоксы в дропдауне
      if (campaignFilterIds !== null && !campaignFilterIds.has(advertId)) return false;
      if (typeFilter !== null && !typeFilter.has(c.typeLabel || '—')) return false;
      if (paymentFilter !== null && !paymentFilter.has(c.paymentLabel || '—')) return false;
      return true;
    }),
    [campaignRows, visibleStatusBuckets, campaignTextFilterIdSet, campaignFilterIds, typeFilter, paymentFilter],
  );

  const aggregateCampaignRowsMetrics = (rows) => {
    let posWeightedSum = 0;
    let posViews = 0;
    const acc = rows.reduce((sum, row) => {
      const m = row?.metrics || {};
      const views = Number(m.impressions ?? m.views) || 0;
      sum.views += views;
      sum.clicks += Number(m.clicks) || 0;
      sum.spend += Number(m.spend) || 0;
      sum.orders += Number(m.orders) || 0;
      sum.atbs += Number(m.atbs) || 0;
      sum.revenue += Number(m.revenue) || 0;
      sum.direct_orders += Number(m.direct_orders) || 0;
      sum.associated_orders += Number(m.associated_orders) || 0;
      sum.direct_revenue += Number(m.direct_revenue) || 0;
      sum.associated_revenue += Number(m.associated_revenue) || 0;
      const pos = parseAvgPosition(m.avg_position);
      if (pos != null && views > 0) {
        posWeightedSum += pos * views;
        posViews += views;
      }
      return sum;
    }, {
      views: 0,
      clicks: 0,
      spend: 0,
      orders: 0,
      atbs: 0,
      revenue: 0,
      direct_orders: 0,
      associated_orders: 0,
      direct_revenue: 0,
      associated_revenue: 0,
    });
    acc.impressions = acc.views;
    acc.ctr = acc.views > 0 ? (acc.clicks / acc.views) * 100 : 0;
    acc.cpc = acc.clicks > 0 ? acc.spend / acc.clicks : 0;
    acc.cpo = acc.orders > 0 ? acc.spend / acc.orders : null;
    acc.cart_cost = acc.atbs > 0 ? acc.spend / acc.atbs : null;
    acc.drr = acc.revenue > 0 ? (acc.spend / acc.revenue) * 100 : null;
    acc.avg_position = posViews > 0 ? posWeightedSum / posViews : null;
    return acc;
  };

  const campaignsTableTotals = useMemo(() => {
    // Итого по таблице = сумма только видимых (отфильтрованных) строк,
    // не period.totals по всему кабинету — иначе при фильтре расход/заказы
    // остаются «по всем», а прямые/ассоц. уже по выборке.
    const metrics = aggregateCampaignRowsMetrics(filteredCampaignRows);
    const hasCompare = filteredCampaignRows.some((c) => c.compareMetrics);
    const compareMetrics = hasCompare
      ? aggregateCampaignRowsMetrics(
          filteredCampaignRows.map((c) => ({ metrics: c.compareMetrics || {} })),
        )
      : null;
    return {
      count: filteredCampaignRows.length,
      metrics,
      compareMetrics,
    };
  }, [filteredCampaignRows]);

  const statusCounters = useMemo(() => {
    const counters = { active: 0, paused: 0, completed: 0 };
    campaignRows.forEach((c) => {
      counters[getStatusBucket(c.status)] += 1;
    });
    return counters;
  }, [campaignRows]);

  const formatNumber = (num) => {
    if (num !== 0 && !num) return '0';
    return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatBudgetAmount = (num) => {
    if (num !== 0 && !num) return '0';
    return Math.round(Number(num)).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
  };

  const getStatusBadge = (status) => {
    const statusInfo = statusMap[String(status)] || { label: 'Неизвестно', color: 'gray', icon: HiX };
    const IconComponent = statusInfo.icon;
    const colorClasses = {
      green: 'bg-green-100 text-green-800',
      orange: 'bg-orange-100 text-orange-800',
      blue: 'bg-blue-100 text-blue-800',
      gray: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${colorClasses[statusInfo.color]}`}>
        <IconComponent className="w-3 h-3" />
        {statusInfo.label}
      </span>
    );
  };

  const getStrategyLabel = (campaign) => {
    if (!campaign.bidder_enabled) return 'Биддер выключен';
    if (campaign.strategy_type === 'target_cpc' && campaign.target_cpc) {
      return `По стоимости клика: ${formatNumber(campaign.target_cpc)} ₽`;
    }
    if (campaign.strategy_type === 'conversion_cost' && campaign.target_conversion_cost) {
      return `По стоимости конверсии: ${formatNumber(campaign.target_conversion_cost)} ₽`;
    }
    if (campaign.strategy_type === 'threshold_scout') {
      return campaign.target_conversion_cost
        ? `Скаут порога: CPO ${formatNumber(campaign.target_conversion_cost)} ₽`
        : 'Скаут порога';
    }
    if (campaign.strategy_type === 'drr' && campaign.target_drr) {
      return `По ДРР: ${formatNumber(campaign.target_drr)}%`;
    }
    return 'Стратегия не настроена';
  };

  const handleCampaignAction = async (advertId, action) => {
    if (action === 'stop') {
      const ok = window.confirm(
        'Завершить кампанию? Это необратимо: повторно запустить её будет нельзя.',
      );
      if (!ok) return;
    }
    setActionLoading((prev) => ({ ...prev, [advertId]: true }));
    try {
      if (action === 'delete') {
        await apiClient.delete(`/campaigns/${advertId}`, { params: { client_id: clientId } });
      } else {
        const endpointByAction = {
          start: `/campaigns/${advertId}/start`,
          pause: `/campaigns/${advertId}/pause`,
          stop: `/campaigns/${advertId}/stop`,
        };
        const endpoint = endpointByAction[action];
        if (!endpoint) throw new Error(`Неизвестное действие: ${action}`);
        await apiClient.post(endpoint, { client_id: clientId });
      }
      await loadCampaignsList();
      await loadBudgetInfo();
      if (action === 'stop') {
        notify('Кампания завершена', 'success');
      }
    } catch (err) {
      console.error(`Ошибка ${action}:`, err);
      notify(`Не удалось выполнить действие: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setActionLoading((prev) => ({ ...prev, [advertId]: false }));
    }
  };

  const moveGrouping = (dragKey, targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    setTableGroupOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(dragKey);
      const to = next.indexOf(targetKey);
      if (from < 0 || to < 0) return prev;
      next.splice(from, 1);
      next.splice(to, 0, dragKey);
      return next;
    });
  };

  const isTableMetricVisible = (metricKey) => visibleTableMetrics.includes(metricKey);

  const toggleTableMetricVisibility = (metricKey) => {
    setVisibleTableMetrics((prev) => {
      if (prev.includes(metricKey)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== metricKey);
      }
      return [...prev, metricKey];
    });
  };

  const campaignDetailsMapRef = useRef({});
  const detailsInFlightRef = useRef(new Set());

  useEffect(() => {
    campaignDetailsMapRef.current = campaignDetailsMap;
  }, [campaignDetailsMap]);

  useEffect(() => {
    setCampaignDetailsMap({});
    campaignDetailsMapRef.current = {};
  }, [periodFrom, periodTo, clientId]);

  const loadCampaignDetails = useCallback(async (advertId) => {
    if (!clientId || !advertId) return;

    const cached = campaignDetailsMapRef.current[advertId];
    if (cached?.periodFrom === periodFrom && cached?.periodTo === periodTo) {
      return;
    }
    if (detailsInFlightRef.current.has(advertId)) return;

    detailsInFlightRef.current.add(advertId);
    setLoadingCampaignDetails((prev) => ({ ...prev, [advertId]: true }));
    try {
      const detailParams = { client_id: clientId, advert_id: advertId, from: periodFrom, to: periodTo };
      const [queriesResp, productsResp] = await Promise.all([
        apiClient.get('/summary', {
          params: { ...detailParams, stats_mode: '1', with_bids: '1', limit: 1500 },
        }),
        apiClient.get('/stats/campaign/products', { params: detailParams }),
      ]);
      const queries = Array.isArray(queriesResp?.data) ? queriesResp.data : [];
      const products = productsResp?.data?.products || [];
      setCampaignDetailsMap((prev) => {
        const next = {
          ...prev,
          [advertId]: {
            periodFrom,
            periodTo,
            products,
            queries,
          },
        };
        campaignDetailsMapRef.current = next;
        return next;
      });
    } catch (e) {
      setCampaignDetailsMap((prev) => {
        const next = {
          ...prev,
          [advertId]: { periodFrom, periodTo, products: [], queries: [] },
        };
        campaignDetailsMapRef.current = next;
        return next;
      });
    } finally {
      detailsInFlightRef.current.delete(advertId);
      setLoadingCampaignDetails((prev) => ({ ...prev, [advertId]: false }));
    }
  }, [clientId, periodFrom, periodTo]);

  const getCampaignMetricTotals = (campaign) => {
    const m = campaign?.metrics || {};
    return {
      views: Number(m.impressions ?? m.views) || 0,
      clicks: Number(m.clicks) || 0,
      spend: Number(m.spend) || 0,
      orders: Number(m.orders) || 0,
      atbs: Number(m.atbs) || 0,
      revenue: Number(m.revenue) || 0,
      avg_position: parseAvgPosition(m.avg_position),
    };
  };

  const summarizeFacts = (facts) => {
    let posWeightedSum = 0;
    let posViews = 0;
    const acc = facts.reduce((sum, f) => {
      const views = Number(f.views) || 0;
      sum.views += views;
      sum.clicks += Number(f.clicks) || 0;
      sum.spend += Number(f.spend) || 0;
      sum.orders += Number(f.orders) || 0;
      sum.atbs += Number(f.atbs) || 0;
      sum.revenue += Number(f.revenue) || 0;
      const pos = parseAvgPosition(f.avg_position);
      if (pos != null && views > 0) {
        posWeightedSum += pos * views;
        posViews += views;
      }
      return sum;
    }, { views: 0, clicks: 0, spend: 0, orders: 0, atbs: 0, revenue: 0 });
    acc.ctr = acc.views > 0 ? (acc.clicks / acc.views) * 100 : 0;
    acc.cpc = acc.clicks > 0 ? acc.spend / acc.clicks : 0;
    acc.cpo = acc.orders > 0 ? acc.spend / acc.orders : null;
    acc.cart_cost = acc.atbs > 0 ? acc.spend / acc.atbs : null;
    acc.drr = acc.revenue > 0 ? (acc.spend / acc.revenue) * 100 : null;
    acc.avg_position = posViews > 0 ? posWeightedSum / posViews : null;
    return acc;
  };

  const subtractMetricTotals = (total, part) => {
    const views = Math.max(0, (total.views || 0) - (part.views || 0));
    const clicks = Math.max(0, (total.clicks || 0) - (part.clicks || 0));
    const spend = Math.max(0, (total.spend || 0) - (part.spend || 0));
    const orders = Math.max(0, (total.orders || 0) - (part.orders || 0));
    const atbs = Math.max(0, (total.atbs || 0) - (part.atbs || 0));
    const revenue = Math.max(0, (total.revenue || 0) - (part.revenue || 0));

    let avg_position = null;
    const totalViews = total.views || 0;
    const partViews = part.views || 0;
    if (views > 0 && total.avg_position != null) {
      if (partViews > 0 && part.avg_position != null) {
        avg_position = (total.avg_position * totalViews - part.avg_position * partViews) / views;
      } else {
        avg_position = total.avg_position;
      }
      if (!Number.isFinite(avg_position)) avg_position = null;
    }

    return { views, clicks, spend, orders, atbs, revenue, avg_position };
  };

  const buildPlacementFactsForCampaign = (campaign, detail) => {
    const queries = detail?.queries || [];
    const products = detail?.products || [];
    const baseFacts = [];
    const campaignLabel = campaign?.name || `Кампания ${campaign?.advert_id}`;
    const advertId = campaign?.advert_id;
    const placementMode = String(campaign?.ad_placement || campaign?.adPlacement || '').toLowerCase();
    const isSearchOnly = placementMode === 'search';
    const isRecOnly = placementMode === 'recommendations' || placementMode === 'recommendation';

    /** Доход по поиску: normquery не отдаёт revenue, берём из wb_product_stats2 и делим по расходу запросов. */
    const totalSearchRevenue = products.reduce((s, p) => s + (Number(p.revenue) || 0), 0);

    if (queries.length > 0) {
      const totalQuerySpend = queries.reduce((s, q) => s + (Number(q.sum) || 0), 0);
      const revenueByNm = new Map();
      products.forEach((p) => {
        const nm = Number(p.nm_id);
        if (!Number.isFinite(nm)) return;
        revenueByNm.set(nm, (revenueByNm.get(nm) || 0) + (Number(p.revenue) || 0));
      });
      const spendByNm = new Map();
      queries.forEach((q) => {
        const nm = q.nm_id ? Number(q.nm_id) : null;
        if (!Number.isFinite(nm)) return;
        spendByNm.set(nm, (spendByNm.get(nm) || 0) + (Number(q.sum) || 0));
      });

      queries.forEach((q) => {
        const spend = Number(q.sum) || 0;
        const nm = q.nm_id ? Number(q.nm_id) : null;
        let revenue = 0;
        if (Number.isFinite(nm) && revenueByNm.has(nm) && (spendByNm.get(nm) || 0) > 0) {
          revenue = (revenueByNm.get(nm) * spend) / spendByNm.get(nm);
        } else if (totalQuerySpend > 0 && totalSearchRevenue > 0) {
          revenue = (totalSearchRevenue * spend) / totalQuerySpend;
        }
        const bidRaw = q.bid != null ? Number(q.bid) : null;
        baseFacts.push({
          campaign: campaignLabel,
          advert_id: advertId,
          placement: PLACEMENT_SEARCH,
          norm_query: q.norm_query || q.keyword || '—',
          nm_id: nm,
          skleyka: nm ? (nmToSkleykaMap[nm] || 'Без склейки') : 'Без склейки',
          views: Number(q.shows) || 0,
          clicks: Number(q.clicks) || 0,
          spend,
          orders: Number(q.orders) || 0,
          atbs: Number(q.atbs) || 0,
          revenue,
          avg_position: parseAvgPosition(q.avg_pos ?? q.avg_position),
          bid_rub: Number.isFinite(bidRaw) && bidRaw > 0 ? bidRaw : null,
        });
      });

      const allocatedRevenue = baseFacts.reduce((s, f) => s + (Number(f.revenue) || 0), 0);
      if (totalSearchRevenue > allocatedRevenue + 0.01 && allocatedRevenue > 0) {
        const factor = totalSearchRevenue / allocatedRevenue;
        baseFacts.forEach((f) => {
          f.revenue = (Number(f.revenue) || 0) * factor;
        });
      } else if (totalSearchRevenue > 0 && allocatedRevenue < 0.01 && totalQuerySpend > 0) {
        baseFacts.forEach((f) => {
          f.revenue = (totalSearchRevenue * (Number(f.spend) || 0)) / totalQuerySpend;
        });
      }
    } else if (products.length > 0) {
      // Без запросов относим product stats к зоне кампании (не выдумываем вторую зону)
      const defaultPlacement = isRecOnly ? PLACEMENT_RECOMMENDATIONS : PLACEMENT_SEARCH;
      products.forEach((p) => {
        baseFacts.push({
          campaign: campaignLabel,
          advert_id: advertId,
          placement: defaultPlacement,
          norm_query: '—',
          nm_id: p.nm_id ? Number(p.nm_id) : null,
          skleyka: p.nm_id ? (nmToSkleykaMap[Number(p.nm_id)] || 'Без склейки') : 'Без склейки',
          views: Number(p.views) || 0,
          clicks: Number(p.clicks) || 0,
          spend: Number(p.spend) || 0,
          orders: Number(p.orders) || 0,
          atbs: Number(p.atbs) || 0,
          revenue: Number(p.revenue) || 0,
          avg_position: parseAvgPosition(p.avg_position),
          bid_rub: null,
        });
      });
    }

    const campaignTotals = getCampaignMetricTotals(campaign);
    let searchTotals = summarizeFacts(baseFacts.filter((f) => f.placement === PLACEMENT_SEARCH));

    /**
     * WB POST /adv/v1/normquery/stats системно недоотдаёт объём относительно fullstats
     * (часто ~70–80% показов даже при полной выгрузке). Для search-only кампаний
     * масштабируем метрики запросов к тоталу кампании, сохраняя доли кластеров —
     * тогда Σ по запросам = статистика кампании.
     */
    if (isSearchOnly && queries.length > 0 && baseFacts.length > 0) {
      ['views', 'clicks', 'spend', 'orders', 'atbs', 'revenue'].forEach((key) => {
        scaleFactsMetric(baseFacts, key, campaignTotals[key]);
      });
      return baseFacts;
    }

    /**
     * Доход и корзины в normquery / product stats не разбиты по зонам показа.
     * Делим доход и atbs пропорционально доле заказов поиска в кампании —
     * только когда кампания реально комбинированная (поиск + рекомендации).
     */
    if (baseFacts.length > 0 && !isSearchOnly && !isRecOnly) {
      const searchShare = computeSearchPlacementShare(searchTotals, campaignTotals);
      scaleFactsMetric(
        baseFacts.filter((f) => f.placement === PLACEMENT_SEARCH),
        'revenue',
        campaignTotals.revenue * searchShare,
      );
      scaleFactsMetric(
        baseFacts.filter((f) => f.placement === PLACEMENT_SEARCH),
        'atbs',
        campaignTotals.atbs * searchShare,
      );
      searchTotals = summarizeFacts(baseFacts.filter((f) => f.placement === PLACEMENT_SEARCH));
    }

    const residual = subtractMetricTotals(campaignTotals, summarizeFacts(baseFacts));
    const residualMeaningful =
      residual.views > 0
      || residual.clicks > 0
      || residual.spend > 0.009
      || residual.orders > 0
      || residual.atbs > 0
      || residual.revenue > 0.009;

    const makeResidualRow = (placement, label) => ({
      campaign: campaignLabel,
      advert_id: advertId,
      placement,
      norm_query: label,
      nm_id: null,
      skleyka: '—',
      ...residual,
      bid_rub: null,
      isSyntheticPlacement: true,
    });

    // Поиск-only без кластеров в периоде: одна строка = тотал кампании
    if (isSearchOnly) {
      if (baseFacts.length === 0) {
        return [makeResidualRow(PLACEMENT_SEARCH, '—')];
      }
      // product-based facts уже близки к кампании; дотягиваем до тотала
      ['views', 'clicks', 'spend', 'orders', 'atbs', 'revenue'].forEach((key) => {
        scaleFactsMetric(baseFacts, key, campaignTotals[key]);
      });
      return baseFacts;
    }

    // Рекомендации-only: не создаём зону «Поиск» из остатка
    if (isRecOnly) {
      if (baseFacts.length === 0) {
        return [makeResidualRow(PLACEMENT_RECOMMENDATIONS, '—')];
      }
      ['views', 'clicks', 'spend', 'orders', 'atbs', 'revenue'].forEach((key) => {
        scaleFactsMetric(baseFacts, key, campaignTotals[key]);
      });
      return baseFacts;
    }

    // Combined / неизвестно: остаток → «Рекомендации»
    const recRow = makeResidualRow(PLACEMENT_RECOMMENDATIONS, '—');
    if (baseFacts.length === 0) {
      return [recRow];
    }
    if (!residualMeaningful) {
      return baseFacts;
    }
    return [...baseFacts, recRow];
  };

  const isCampaignFirst = tableGroupOrder[0] === 'campaign';
  const nestedGroupOrder = useMemo(
    () => (isCampaignFirst ? tableGroupOrder.slice(1) : tableGroupOrder),
    [isCampaignFirst, tableGroupOrder]
  );

  const campaignByAdvertId = useMemo(() => {
    const map = {};
    filteredCampaignRows.forEach((c) => {
      map[c.advert_id] = c;
    });
    return map;
  }, [filteredCampaignRows]);

  const factsByCampaign = useMemo(() => {
    const result = {};
    campaignRows.forEach((campaign) => {
      const advertId = campaign.advert_id;
      const detail = campaignDetailsMap[advertId];
      if (!detail) {
        result[advertId] = [];
        return;
      }
      result[advertId] = buildPlacementFactsForCampaign(campaign, detail);
    });
    return result;
  }, [campaignRows, campaignDetailsMap, nmToSkleykaMap]);

  const buildGroupTree = useCallback((facts, groupOrder, rootKey) => {
    const buildLevel = (items, level, parentPath) => {
      const dim = groupOrder[level];
      if (!dim || !items.length) return [];
      const map = new Map();
      items.forEach((f) => {
        const raw = f[dim];
        const label = raw !== null && raw !== undefined && String(raw).trim() !== '' ? String(raw) : '—';
        if (!map.has(label)) map.set(label, []);
        map.get(label).push(f);
      });
      return Array.from(map.entries()).map(([label, groupItems]) => {
        const nodeId = `${rootKey}:${parentPath}:${dim}:${label}`;
        let bid_rub = null;
        if (dim === 'norm_query') {
          const withBid = groupItems
            .filter((f) => f.bid_rub != null && Number.isFinite(Number(f.bid_rub)))
            .sort((a, b) => (Number(b.spend) || 0) - (Number(a.spend) || 0));
          if (withBid.length) bid_rub = Number(withBid[0].bid_rub);
        }
        return {
          id: nodeId,
          level,
          dim,
          label,
          advertId: dim === 'campaign' ? groupItems[0]?.advert_id : undefined,
          bid_rub,
          metrics: summarizeFacts(groupItems),
          children: buildLevel(groupItems, level + 1, `${parentPath}/${dim}=${label}`),
        };
      });
    };
    return buildLevel(facts, 0, 'root');
  }, []);

  const allGroupingFacts = useMemo(() => {
    const facts = [];
    filteredCampaignRows.forEach((campaign) => {
      const rows = factsByCampaign[campaign.advert_id];
      if (rows?.length) facts.push(...rows);
    });
    return facts;
  }, [filteredCampaignRows, factsByCampaign]);

  const rootTreeNodes = useMemo(() => {
    if (isCampaignFirst) return [];
    return buildGroupTree(allGroupingFacts, tableGroupOrder, 'root');
  }, [isCampaignFirst, allGroupingFacts, tableGroupOrder, buildGroupTree]);

  const filteredCampaignIdsKey = useMemo(
    () => filteredCampaignRows.map((c) => c.advert_id).sort((a, b) => a - b).join(','),
    [filteredCampaignRows]
  );

  const groupingDetailsLoading = useMemo(() => {
    if (isCampaignFirst || !filteredCampaignRows.length) return false;
    return filteredCampaignRows.some((c) => {
      const id = c.advert_id;
      return loadingCampaignDetails[id] || !campaignDetailsMap[id];
    });
  }, [isCampaignFirst, filteredCampaignRows, loadingCampaignDetails, campaignDetailsMap]);

  const expandedGroupNodesKey = useMemo(
    () => Array.from(expandedGroupNodes).sort().join('|'),
    [expandedGroupNodes]
  );

  const toggleGroupNode = (nodeId) => {
    setExpandedGroupNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  useEffect(() => {
    if (!clientId || !filteredCampaignIdsKey) return;
    if (!isCampaignFirst) {
      filteredCampaignIdsKey.split(',').filter(Boolean).forEach((id) => {
        loadCampaignDetails(Number(id));
      });
      return;
    }
    Array.from(expandedGroupNodes).forEach((nodeId) => {
      if (!nodeId.startsWith('campaign-root:')) return;
      const advertId = Number(nodeId.slice('campaign-root:'.length));
      if (Number.isFinite(advertId)) loadCampaignDetails(advertId);
    });
  }, [clientId, periodFrom, periodTo, filteredCampaignIdsKey, isCampaignFirst, expandedGroupNodesKey, loadCampaignDetails]);

  const getDelta = (current, compare) => {
    if (compare === null || compare === undefined) return null;
    const delta = current - compare;
    const percent = compare !== 0 ? ((delta / compare) * 100) : (current !== 0 ? 100 : 0);
    return { delta, percent };
  };

  const formatWithDelta = (current, compare, formatter, metricKey) => {
    const deltaData = getDelta(current, compare);
    if (deltaData === null) {
      return (
        <div className="flex flex-col gap-0.5">
          <div className="font-medium">{formatter(current)}</div>
        </div>
      );
    }
    const { delta, percent } = deltaData;
    const isPositive = delta >= 0;
    const percentText = `${isPositive ? '+' : ''}${percent.toFixed(1)}%`;
    const deltaColorClass = metricKey
      ? getCompareDeltaColorClass(metricKey, delta)
      : (isPositive ? 'text-green-600' : 'text-red-600');

    return (
      <div className="flex flex-col gap-0.5">
        <div className="font-medium">{formatter(current)}</div>
        <div className={`text-xs font-medium ${deltaColorClass}`}>
          {percentText}
        </div>
        <div className="text-xs text-gray-500">
          {formatter(compare)}
        </div>
      </div>
    );
  };

  const renderGroupingRow = (row) => {
    const canExpand = row.children && row.children.length > 0;
    const isExpanded = expandedGroupNodes.has(row.id);
    return (
      <tr key={row.id} className="border-b border-slate-100 bg-slate-50/70 hover:bg-slate-50">
        <td {...pinnedCellProps(0, 'py-2 px-3', { bg: 'bg-slate-50' })} />
        <td {...pinnedCellProps(1, 'py-2 px-4', { bg: 'bg-slate-50' })}>
          <div className="flex items-center gap-2" style={{ paddingLeft: `${row.level * 16}px` }}>
            {canExpand ? (
              <button
                type="button"
                onClick={() => toggleGroupNode(row.id)}
                className="p-0.5 rounded hover:bg-slate-200 text-slate-600"
              >
                {isExpanded ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <span className="text-xs text-slate-500 uppercase">
              {tableGroupingOptions.find((o) => o.key === row.dim)?.label}
            </span>
            <span className="font-medium text-slate-700">{row.label}</span>
            {row.dim === 'campaign' && row.advertId != null && (
              <span className="text-xs text-gray-400">ID: {row.advertId}</span>
            )}
          </div>
        </td>
        <td {...pinnedCellProps(2, 'py-2 px-4 text-slate-400', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(3, 'py-2 px-4 text-slate-400', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(4, 'py-2 px-4 text-right', { bg: 'bg-slate-50' })}>
          {row.dim === 'norm_query' && row.bid_rub != null
            ? (
              <div className="font-medium text-slate-700">
                {Number(row.bid_rub).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
              </div>
            )
            : <span className="text-slate-400">—</span>}
        </td>
        <td {...pinnedCellProps(5, 'py-2 px-4 text-slate-400', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(6, 'py-2 px-4 text-slate-400', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(7, 'py-2 px-4 text-center text-slate-400 align-middle', { bg: 'bg-slate-50' })}>—</td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('views') ? '' : 'hidden'}`}>
          {(row.metrics.views || 0).toLocaleString('ru-RU')}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('clicks') ? '' : 'hidden'}`}>
          {(row.metrics.clicks || 0).toLocaleString('ru-RU')}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('spend') ? '' : 'hidden'}`}>
          {(row.metrics.spend || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('orders') ? '' : 'hidden'}`}>
          {(row.metrics.orders || 0).toLocaleString('ru-RU')}
        </td>
        <td className={`py-2 px-4 text-right text-slate-400 ${isTableMetricVisible('direct_orders') ? '' : 'hidden'}`}>—</td>
        <td className={`py-2 px-4 text-right text-slate-400 ${isTableMetricVisible('associated_orders') ? '' : 'hidden'}`}>—</td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('atbs') ? '' : 'hidden'}`}>
          {(row.metrics.atbs || 0).toLocaleString('ru-RU')}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('revenue') ? '' : 'hidden'}`}>
          {(row.metrics.revenue || 0).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </td>
        <td className={`py-2 px-4 text-right text-slate-400 ${isTableMetricVisible('direct_revenue') ? '' : 'hidden'}`}>—</td>
        <td className={`py-2 px-4 text-right text-slate-400 ${isTableMetricVisible('associated_revenue') ? '' : 'hidden'}`}>—</td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('ctr') ? '' : 'hidden'}`}>
          {(row.metrics.ctr || 0).toFixed(2)}%
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('cpc') ? '' : 'hidden'}`}>
          {(row.metrics.cpc || 0).toFixed(2)} ₽
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('cpo') ? '' : 'hidden'}`}>
          {row.metrics.cpo != null ? `${row.metrics.cpo.toFixed(2)} ₽` : '—'}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('cart_cost') ? '' : 'hidden'}`}>
          {row.metrics.cart_cost != null ? `${row.metrics.cart_cost.toFixed(2)} ₽` : '—'}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('avg_position') ? '' : 'hidden'}`}>
          {row.metrics.avg_position != null ? Number(row.metrics.avg_position).toFixed(2) : '—'}
        </td>
        <td className={`py-2 px-4 text-right ${isTableMetricVisible('drr') ? '' : 'hidden'}`}>
          {row.metrics.drr != null ? `${row.metrics.drr.toFixed(2)}%` : '—'}
        </td>
      </tr>
    );
  };

  const renderCampaignsTotalsRow = () => {
    const { metrics, compareMetrics, count } = campaignsTableTotals;
    const compare = compareMetrics;
    return (
      <tr
        key="campaigns-table-totals"
        className="border-b border-slate-300 bg-slate-50/90 font-medium text-gray-900"
      >
        <td {...pinnedCellProps(0, 'py-3 px-3', { bg: 'bg-slate-50' })} />
        <td {...pinnedCellProps(1, 'py-3 px-4', { bg: 'bg-slate-50' })}>
          <div className="font-semibold text-gray-900">Итого</div>
          <div className="text-xs font-normal text-gray-600 mt-0.5">
            {count.toLocaleString('ru-RU')} {count === 1 ? 'кампания' : count < 5 ? 'кампании' : 'кампаний'}
          </div>
        </td>
        <td {...pinnedCellProps(2, 'py-3 px-4 text-gray-500', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(3, 'py-3 px-4 text-gray-500', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(4, 'py-3 px-4 text-gray-500', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(5, 'py-3 px-4 text-gray-500', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(6, 'py-3 px-4 text-gray-500', { bg: 'bg-slate-50' })}>—</td>
        <td {...pinnedCellProps(7, 'py-3 px-4 text-center text-gray-500', { bg: 'bg-slate-50' })}>—</td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('views') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.impressions, compare?.impressions ?? compare?.views, (v) => v.toLocaleString('ru-RU'), 'views')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('clicks') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.clicks, compare?.clicks, (v) => v.toLocaleString('ru-RU'), 'clicks')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('spend') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.spend, compare?.spend, (v) => v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'spend')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('orders') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.orders, compare?.orders, (v) => v.toLocaleString('ru-RU'), 'orders')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('direct_orders') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.direct_orders, compare?.direct_orders, (v) => v.toLocaleString('ru-RU'), 'direct_orders')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('associated_orders') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.associated_orders, compare?.associated_orders, (v) => v.toLocaleString('ru-RU'), 'associated_orders')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('atbs') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.atbs, compare?.atbs, (v) => v.toLocaleString('ru-RU'), 'atbs')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('revenue') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.revenue, compare?.revenue, (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'revenue')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('direct_revenue') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.direct_revenue, compare?.direct_revenue, (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'direct_revenue')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('associated_revenue') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.associated_revenue, compare?.associated_revenue, (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'associated_revenue')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('ctr') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.ctr, compare?.ctr, (v) => `${v.toFixed(2)}%`, 'ctr')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('cpc') ? '' : 'hidden'}`}>
          {formatWithDelta(metrics.cpc, compare?.cpc, (v) => `${v.toFixed(2)} ₽`, 'cpc')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('cpo') ? '' : 'hidden'}`}>
          {metrics.cpo !== null ? (
            formatWithDelta(metrics.cpo, compare?.cpo, (v) => (v !== null ? `${v.toFixed(2)} ₽` : '—'), 'cpo')
          ) : (
            <div>—</div>
          )}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('cart_cost') ? '' : 'hidden'}`}>
          {metrics.cart_cost !== null ? (
            formatWithDelta(metrics.cart_cost, compare?.cart_cost, (v) => (v !== null ? `${v.toFixed(2)} ₽` : '—'), 'cart_cost')
          ) : (
            <div>—</div>
          )}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('avg_position') ? '' : 'hidden'}`}>
          {metrics.avg_position != null ? Number(metrics.avg_position).toFixed(2) : '—'}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('drr') ? '' : 'hidden'}`}>
          {metrics.drr != null ? (
            formatWithDelta(metrics.drr, compare?.drr, (v) => `${v.toFixed(2)}%`, 'drr')
          ) : (
            '—'
          )}
        </td>
      </tr>
    );
  };

  const renderCampaignRow = (campaign, { nodeId, level = 0, treeChildren = null }) => {
    const advertId = campaign.advert_id;
    const compare = campaign.compareMetrics;
    const isExpanded = expandedGroupNodes.has(nodeId);
    const hasTreeChildren = treeChildren && treeChildren.length > 0;
    const canExpand = hasTreeChildren || (nodeId.startsWith('campaign-root:') && nestedGroupOrder.length > 0);
    const rows = [];

    rows.push(
      <tr key={nodeId} className="border-b border-slate-100 hover:bg-white/90">
        <td {...pinnedCellProps(0, 'py-3 px-3', { bg: 'bg-white' })}>
          <input
            type="checkbox"
            checked={selectedCampaigns.has(advertId)}
            onChange={(e) => {
              const newSet = new Set(selectedCampaigns);
              if (e.target.checked) newSet.add(advertId);
              else newSet.delete(advertId);
              setSelectedCampaigns(newSet);
            }}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </td>
        <td {...pinnedCellProps(1, 'py-3 px-4', { bg: 'bg-white' })}>
          <div className="flex items-start gap-2" style={{ paddingLeft: `${level * 16}px` }}>
            {canExpand ? (
              <button
                type="button"
                onClick={() => toggleGroupNode(nodeId)}
                className="mt-0.5 p-0.5 rounded hover:bg-slate-100 text-slate-600"
                title={isExpanded ? 'Свернуть' : 'Раскрыть'}
              >
                {isExpanded ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
              </button>
            ) : (
              <span className="w-4 mt-0.5" />
            )}
            <div>
              <div className="font-medium">{campaign.name}</div>
              <div className="text-xs text-gray-400">ID: {advertId}</div>
              <div className="text-xs text-gray-500 mt-1 flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setSelectedCampaignForStrategy(campaign); setStrategyModalOpen(true); }}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Стратегия
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => { setSelectedCampaignForSchedule(campaign); setScheduleModalOpen(true); }}
                  className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  <HiClock className="w-3 h-3" />
                  Режим показов
                </button>
              </div>
            </div>
          </div>
        </td>
        <td {...pinnedCellProps(2, 'py-3 px-4 text-gray-700', { bg: 'bg-white' })}>{campaign.typeLabel || '—'}</td>
        <td {...pinnedCellProps(3, 'py-3 px-4 text-gray-700', { bg: 'bg-white' })}>{campaign.paymentLabel || '—'}</td>
        <td {...pinnedCellProps(4, 'py-3 px-4 text-right', { bg: 'bg-white' })}>
          <div className="font-medium">
            Текущая: {campaign.current_bid_rub != null ? `${Number(campaign.current_bid_rub).toFixed(2)}` : '—'}
          </div>
          <div className="text-xs text-gray-500">
            Лидер: {campaign.leaders_bid_rub != null ? `${Number(campaign.leaders_bid_rub).toFixed(2)}` : '—'}
          </div>
          <div className="text-xs text-gray-500">
            Конкурент: {campaign.competitive_bid_rub != null ? `${Number(campaign.competitive_bid_rub).toFixed(2)}` : '—'}
          </div>
        </td>
        <td {...pinnedCellProps(5, 'py-3 px-4', { bg: 'bg-white' })}>
          {campaign.status !== undefined && (campaign.status === 9 || campaign.status === 11) ? (
            <>
              <button
                type="button"
                onClick={() => handleCampaignAction(advertId, campaign.status === 9 ? 'pause' : 'start')}
                disabled={actionLoading[advertId]}
                className={`relative inline-flex h-8 w-16 items-center rounded-full border backdrop-blur-md shadow-sm transition-all duration-200 disabled:opacity-50 ${
                  campaign.status === 9
                    ? 'bg-emerald-400/45 border-emerald-200/70'
                    : 'bg-slate-300/45 border-slate-200/70'
                }`}
                title={campaign.status === 9 ? 'Поставить на паузу' : 'Запустить'}
              >
                <span
                  className={`inline-block h-6 w-6 rounded-full bg-white/95 shadow-md ring-1 ring-white/70 transition-transform duration-200 ${
                    campaign.status === 9 ? 'translate-x-9' : 'translate-x-1'
                  }`}
                />
              </button>
              <div className={`text-[10px] mt-1 font-semibold ${campaign.status === 9 ? 'text-emerald-700' : 'text-slate-600'}`}>
                {campaign.status === 9 ? 'Запущена' : 'На паузе'}
              </div>
              <button
                type="button"
                onClick={() => handleCampaignAction(advertId, 'stop')}
                disabled={actionLoading[advertId]}
                className="mt-1.5 text-[11px] font-medium text-gray-600 hover:text-red-700 underline-offset-2 hover:underline disabled:opacity-50"
                title="Завершить кампанию (необратимо)"
              >
                Завершить
              </button>
              <div className="text-xs text-gray-500 mt-1">
                Текущий бюджет: {(() => {
                  const raw = campaign.budget ?? campaign.dailyBudget ?? campaign.daily_budget;
                  if (raw == null || raw === '') return '—';
                  const n = Number(raw);
                  return Number.isFinite(n) ? `${formatNumber(n)} ₽` : '—';
                })()}
              </div>
            </>
          ) : campaign.status !== undefined ? (
            getStatusBadge(campaign.status)
          ) : (
            '—'
          )}
        </td>
        <td {...pinnedCellProps(6, 'py-3 px-4', { bg: 'bg-white' })}>
          {campaign.daily_spend_limit ? (
            <div className="font-medium text-blue-600">{formatNumber(campaign.daily_spend_limit)} ₽/день</div>
          ) : (
            <div className="text-gray-400 text-xs">Лимит не установлен</div>
          )}
          <div className="text-xs text-gray-500 mt-0.5">{getStrategyLabel(campaign)}</div>
        </td>
        <td {...pinnedCellProps(7, 'py-3 px-4 text-center align-middle whitespace-nowrap', { bg: 'bg-white' })}>
          <div className="font-medium text-gray-900">
            {campaign.promoted_stock_remain != null
              ? Number(campaign.promoted_stock_remain).toLocaleString('ru-RU')
              : '—'}
          </div>
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('views') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.impressions, compare?.impressions, (v) => v.toLocaleString('ru-RU'), 'views')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('clicks') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.clicks, compare?.clicks, (v) => v.toLocaleString('ru-RU'), 'clicks')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('spend') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.spend, compare?.spend, (v) => v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'spend')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('orders') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.orders, compare?.orders, (v) => v.toLocaleString('ru-RU'), 'orders')}
        </td>
        <td
          className={`py-3 px-4 text-right ${isTableMetricVisible('direct_orders') ? '' : 'hidden'}`}
          title="Заказы по основному продвигаемому артикулу"
        >
          {formatWithDelta(
            campaign.metrics.direct_orders ?? 0,
            compare?.direct_orders,
            (v) => v.toLocaleString('ru-RU'),
            'direct_orders',
          )}
        </td>
        <td
          className={`py-3 px-4 text-right ${isTableMetricVisible('associated_orders') ? '' : 'hidden'}`}
          title="Остальные заказы кампании: другие артикулы и заказы без привязки к артикулу"
        >
          {formatWithDelta(
            campaign.metrics.associated_orders ?? 0,
            compare?.associated_orders,
            (v) => v.toLocaleString('ru-RU'),
            'associated_orders',
          )}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('atbs') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.atbs || 0, compare?.atbs, (v) => v.toLocaleString('ru-RU'), 'atbs')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('revenue') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.revenue || 0, compare?.revenue, (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'revenue')}
        </td>
        <td
          className={`py-3 px-4 text-right ${isTableMetricVisible('direct_revenue') ? '' : 'hidden'}`}
          title="Доход по продвигаемому артикулу"
        >
          {formatWithDelta(
            campaign.metrics.direct_revenue ?? 0,
            compare?.direct_revenue,
            (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            'direct_revenue',
          )}
        </td>
        <td
          className={`py-3 px-4 text-right ${isTableMetricVisible('associated_revenue') ? '' : 'hidden'}`}
          title="Остальной доход кампании (всего − прямой)"
        >
          {formatWithDelta(
            campaign.metrics.associated_revenue ?? 0,
            compare?.associated_revenue,
            (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            'associated_revenue',
          )}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('ctr') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.ctr, compare?.ctr, (v) => `${v.toFixed(2)}%`, 'ctr')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('cpc') ? '' : 'hidden'}`}>
          {formatWithDelta(campaign.metrics.cpc, compare?.cpc, (v) => `${v.toFixed(2)} ₽`, 'cpc')}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('cpo') ? '' : 'hidden'}`}>
          {campaign.metrics.cpo !== null ? (
            formatWithDelta(campaign.metrics.cpo, compare?.cpo, (v) => (v !== null ? `${v.toFixed(2)} ₽` : '—'), 'cpo')
          ) : (
            <div>—</div>
          )}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('cart_cost') ? '' : 'hidden'}`}>
          {campaign.metrics.cart_cost !== null ? (
            formatWithDelta(campaign.metrics.cart_cost, compare?.cart_cost, (v) => (v !== null ? `${v.toFixed(2)} ₽` : '—'), 'cart_cost')
          ) : (
            <div>—</div>
          )}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('avg_position') ? '' : 'hidden'}`}>
          {campaign.metrics.avg_position != null ? Number(campaign.metrics.avg_position).toFixed(2) : '—'}
        </td>
        <td className={`py-3 px-4 text-right ${isTableMetricVisible('drr') ? '' : 'hidden'}`}>
          {(() => {
            const drr = campaign.metrics.revenue > 0 && campaign.metrics.revenue !== undefined
              ? (campaign.metrics.spend / campaign.metrics.revenue) * 100
              : 0;
            const compareDrr = compare?.revenue > 0 && compare?.revenue !== undefined
              ? (compare.spend / compare.revenue) * 100
              : null;
            return formatWithDelta(drr, compareDrr, (v) => `${v.toFixed(2)}%`, 'drr');
          })()}
        </td>
      </tr>
    );

    if (isExpanded && canExpand) {
      if (loadingCampaignDetails[advertId]) {
        rows.push(
          <tr key={`${nodeId}:loading`} className="border-b border-slate-100 bg-slate-50">
            <td colSpan={24} className="py-2 px-4 text-sm text-gray-500" style={{ paddingLeft: `${(level + 1) * 16}px` }}>
              Загрузка группировок…
            </td>
          </tr>
        );
      } else {
        const childNodes = treeChildren ?? buildGroupTree(
          factsByCampaign[advertId] || [],
          nestedGroupOrder,
          String(advertId)
        );
        childNodes.forEach((child) => {
          rows.push(...renderTreeNode(child));
        });
      }
    }

    return rows;
  };

  const renderTreeNode = (node) => {
    if (node.dim === 'campaign') {
      const campaign = campaignByAdvertId[node.advertId];
      if (campaign) {
        return renderCampaignRow(campaign, {
          nodeId: node.id,
          level: node.level,
          treeChildren: node.children,
        });
      }
    }

    const out = [renderGroupingRow(node)];
    const isExpanded = expandedGroupNodes.has(node.id);
    if (isExpanded && node.children?.length) {
      node.children.forEach((child) => {
        out.push(...renderTreeNode(child));
      });
    }
    return out;
  };

  const tableBodyRows = useMemo(() => {
    if (isCampaignFirst) {
      const campaignRowsList = filteredCampaignRows.flatMap((campaign) => {
        const nodeId = `campaign-root:${campaign.advert_id}`;
        return renderCampaignRow(campaign, { nodeId, level: 0, treeChildren: null });
      });
      if (filteredCampaignRows.length === 0) return campaignRowsList;
      return [renderCampaignsTotalsRow(), ...campaignRowsList];
    }
    if (groupingDetailsLoading) {
      return [
        <tr key="grouping-loading" className="border-b border-slate-100 bg-slate-50">
          <td colSpan={24} className="py-3 px-4 text-sm text-gray-500">Загрузка группировок…</td>
        </tr>,
      ];
    }
    if (!rootTreeNodes.length) {
      return [
        <tr key="grouping-empty" className="border-b border-slate-100 bg-slate-50">
          <td colSpan={24} className="py-3 px-4 text-sm text-gray-500">Нет данных для группировки за выбранный период.</td>
        </tr>,
      ];
    }
    return rootTreeNodes.flatMap((node) => renderTreeNode(node));
  }, [
    isCampaignFirst,
    filteredCampaignRows,
    rootTreeNodes,
    groupingDetailsLoading,
    expandedGroupNodesKey,
    factsByCampaign,
    nestedGroupOrder,
    campaignDetailsMap,
    loadingCampaignDetails,
    selectedCampaigns,
    actionLoading,
    visibleTableMetrics,
    campaignsTableTotals,
  ]);

  return (
    <div className="space-y-6">
      {/* Период */}
      <div className="flex justify-end items-start">
        <div className="flex items-center gap-4">
          {/* Компактный виджет выбора даты */}
          <button
            onClick={() => setDateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <HiCalendar className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {formatDateRange(periodFrom, periodTo)}
            </span>
          </button>
          </div>
        </div>

      {error && (
        <div className="text-center text-red-600">{error}</div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-6">
              {/* ЛЕВАЯ ЗОНА: круговая диаграмма бюджета + компактная легенда */}
              <div className="lg:col-span-3 rounded-xl border border-white/50 bg-white/90 backdrop-blur-sm shadow-sm p-3 flex flex-col">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h3 className="text-sm font-semibold text-gray-800 truncate">Бюджет месяца</h3>
                  <button
                    type="button"
                    onClick={() => setBudgetModalOpen(true)}
                    className="glass-btn py-1 px-2 text-xs font-medium inline-flex items-center justify-center gap-1 shrink-0"
                  >
                    <HiCurrencyDollar className="w-3.5 h-3.5 shrink-0" />
                    <span>{budgetOverview.hasBudget ? 'Изменить' : 'Задать'}</span>
                  </button>
                </div>

                {budgetOverview.hasBudget ? (
                  <div className="flex-1 grid grid-cols-[minmax(0,1fr)_minmax(132px,38%)] gap-1.5 min-h-[168px]">
                    {/* Диаграмма */}
                    <div className="relative min-w-0 budget-donut-chart">
                      <ResponsiveContainer width="100%" height="100%" minHeight={168}>
                        <PieChart accessibilityLayer={false}>
                          <Pie
                            data={budgetOverview.pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="58%"
                            outerRadius="88%"
                            paddingAngle={2}
                            startAngle={90}
                            endAngle={-270}
                            stroke="none"
                            rootTabIndex={-1}
                          >
                            {budgetOverview.pieData.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={
                                  entry.name === 'Потрачено' && budgetOverview.remaining < 0
                                    ? '#ef4444'
                                    : (BUDGET_PIE_COLORS[entry.name] || '#94a3b8')
                                }
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) => [`${formatBudgetAmount(value)} ₽`, name]}
                            contentStyle={{
                              backgroundColor: 'rgba(30, 41, 59, 0.95)',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '8px 12px',
                            }}
                            labelStyle={{ color: '#94a3b8', fontSize: 12 }}
                            itemStyle={{ color: '#f1f5f9', fontSize: 13 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-1">
                        <span className="text-xs font-medium text-gray-600">
                          {budgetOverview.hasForecast ? 'Прогноз' : 'Использовано'}
                        </span>
                        <span className="text-lg font-bold text-gray-900 leading-tight">
                          {budgetOverview.hasForecast && budgetOverview.forecastPct != null
                            ? `${budgetOverview.forecastPct.toFixed(1)}%`
                            : budgetOverview.total > 0
                              ? `${budgetOverview.usedPct.toFixed(1)}%`
                              : '—'}
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5 text-center leading-tight whitespace-nowrap tabular-nums">
                          {budgetOverview.hasForecast && budgetOverview.forecast != null
                            ? `~${formatBudgetAmount(budgetOverview.forecast)} ₽`
                            : `из ${formatBudgetAmount(budgetOverview.total)} ₽`}
                        </span>
                      </div>
                    </div>

                    {/* Легенда бюджета — как карточки метрик на вкладке «Воронка продаж» */}
                    <div className="flex flex-col gap-1 min-w-[132px]">
                      <div className={`min-w-0 ${BUDGET_LEGEND_CARD_CLASS}`}>
                        <div className="mb-2">
                          <p className={SUMMARY_LEGEND_LABEL_CLASS}>Потрачено</p>
                        </div>
                        <p className={`${BUDGET_LEGEND_VALUE_CLASS} whitespace-nowrap tabular-nums`}>{formatBudgetAmount(budgetOverview.spent)} ₽</p>
                      </div>
                      {budgetOverview.hasForecast && (
                        <div className={`min-w-0 ${BUDGET_LEGEND_CARD_CLASS}`}>
                          <div className="mb-2">
                            <p className={SUMMARY_LEGEND_LABEL_CLASS}>Прогноз</p>
                          </div>
                          <p className={`${BUDGET_LEGEND_VALUE_CLASS} whitespace-nowrap tabular-nums ${
                            budgetOverview.forecastOvershoot > 0 ? 'text-red-600' : 'text-amber-600'
                          }`}>
                            {formatBudgetAmount(budgetOverview.forecast)} ₽
                          </p>
                        </div>
                      )}
                      <div className={`min-w-0 ${BUDGET_LEGEND_CARD_CLASS}`}>
                        <div className="mb-2">
                          <p className={SUMMARY_LEGEND_LABEL_CLASS}>Остаток</p>
                        </div>
                        <p className={`${BUDGET_LEGEND_VALUE_CLASS} whitespace-nowrap tabular-nums ${
                          budgetOverview.hasForecast
                            ? (budgetOverview.remainingAfterForecast <= 0 ? 'text-red-600' : 'text-green-600')
                            : (budgetOverview.remaining < 0 ? 'text-red-600' : 'text-green-600')
                        }`}>
                          {formatBudgetAmount(
                            budgetOverview.hasForecast
                              ? budgetOverview.remainingAfterForecast
                              : budgetOverview.remaining
                          )} ₽
                        </p>
                      </div>
                      <div className={`min-w-0 ${BUDGET_LEGEND_CARD_CLASS}`}>
                        <div className="mb-2">
                          <p className={SUMMARY_LEGEND_LABEL_CLASS}>Бюджет</p>
                        </div>
                        <p className={`${BUDGET_LEGEND_VALUE_CLASS} whitespace-nowrap tabular-nums`}>{formatBudgetAmount(budgetOverview.total)} ₽</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-h-[168px] flex flex-col items-center justify-center text-center px-2">
                    <p className="text-xs text-gray-500 mb-3">Месячный бюджет не задан</p>
                    <button
                      type="button"
                      onClick={() => setBudgetModalOpen(true)}
                      className="glass-btn py-2 px-3 text-xs font-medium inline-flex items-center gap-1.5"
                    >
                      <HiCurrencyDollar className="w-3.5 h-3.5" />
                      Задать бюджет
                    </button>
                  </div>
                )}
              </div>

              {/* ПРАВАЯ ЗОНА: 9 метрик в сетке 3×3 */}
              <div className="lg:col-span-9 grid grid-cols-3 gap-1.5 auto-rows-fr">
                {summaryCards.map((card) => {
                  const delta = card.compare !== undefined ? card.value - card.compare : null;
                  const deltaPercent = card.compare !== undefined && card.compare !== 0
                    ? ((delta / card.compare) * 100).toFixed(2)
                    : null;
                  const deltaText = deltaPercent !== null
                    ? `${deltaPercent >= 0 ? '+' : ''}${deltaPercent}%`
                    : null;

                  return (
                    <div key={card.key} className={SUMMARY_LEGEND_CARD_CLASS}>
                      <div className="mb-2">
                        <p className={SUMMARY_LEGEND_LABEL_CLASS}>{card.label}</p>
                      </div>
                      <p className={SUMMARY_LEGEND_VALUE_CLASS}>{card.format(card.value)}</p>
                      {card.compare !== undefined && deltaText && (
                        <div className="flex items-center gap-1 mt-auto pt-1">
                          <span className={`text-xs font-semibold ${getCompareDeltaColorClass(card.key, delta)}`}>
                            {deltaText}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          <div className="rounded-xl border border-white/50 bg-white/95 backdrop-blur-sm shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Динамика</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {stats.period.from} → {stats.period.to}{' '}
                  {stats.compare && `(сравнение: ${stats.compare.from} → ${stats.compare.to})`}
                </p>
              </div>
              <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-100/80">
                <span className="text-xs text-gray-500 px-2 py-1.5">Группировка:</span>
                {[
                  { key: 'day', label: 'По дням', short: 'D' },
                  { key: 'week', label: 'По неделям', short: 'W' },
                  { key: 'month', label: 'По месяцам', short: 'M' },
                ].map(({ key, label, short }) => (
                  <button
                    key={key}
                    onClick={() => setGroupBy(key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      groupBy === key
                        ? 'bg-indigo-500 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-white'
                    }`}
                    title={label}
                  >
                    {short}
                  </button>
                ))}
              </div>
            </div>
            <ChartPanel height={320}>
              <ChartErrorBoundary label="кампании">
                {chartData.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-500">
                    Нет данных за выбранный период
                  </div>
                ) : (
                <ResponsiveContainer width="100%" height={320} debounce={50}>
                <ComposedChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                  <defs>
                    {selectedMetrics.map((metric) => {
                      const color = metricColors[metric] || '#6366f1';
                      return (
                        <linearGradient key={metric} id={`chart-fill-${metric}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                          <stop offset="70%" stopColor={color} stopOpacity={0.2} />
                          <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: 'none',
                      borderRadius: '10px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                      padding: '12px 14px',
                    }}
                    labelStyle={{ color: '#94a3b8', fontSize: 12 }}
                    itemStyle={{ color: '#f1f5f9', fontSize: 13 }}
                    formatter={(value, name) => {
                      const metric = String(name).replace('period_', '').replace('compare_', '');
                      const fmt = metricOptions.find((m) => m.key === metric)?.format;
                      return [fmt ? fmt(value) : value, metricOptions.find((m) => m.key === metric)?.label || metric];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: 12 }}
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => {
                      const metric = String(value).replace(/^period_/, '').replace(/^compare_/, '');
                      return metricOptions.find((m) => m.key === metric)?.label || value;
                    }}
                  />
                  {selectedMetrics.map((metric) => {
                    const color = metricColors[metric] || '#6366f1';
                    return (
                      <React.Fragment key={metric}>
                        <Area
                          type="monotone"
                          dataKey={`period_${metric}`}
                          fill={`url(#chart-fill-${metric})`}
                          stroke={color}
                          strokeWidth={2.5}
                          baseValue={0}
                          connectNulls
                          isAnimationActive={false}
                        />
                        {showCompareLines && stats.compare && (
                          <Line
                            type="monotone"
                            dataKey={`compare_${metric}`}
                            stroke={color}
                            strokeOpacity={0.6}
                            dot={false}
                            strokeDasharray="5 5"
                            strokeWidth={1.5}
                            connectNulls
                            isAnimationActive={false}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </ComposedChart>
                </ResponsiveContainer>
                )}
              </ChartErrorBoundary>
            </ChartPanel>
          </div>

          <div className="rounded-xl border border-white/50 bg-white/85 backdrop-blur-sm shadow-sm p-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Кампании</h3>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateCampaignModalOpen(true)}
                    className="px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-sm text-indigo-700 hover:bg-indigo-100 inline-flex items-center gap-1.5"
                  >
                    <HiPlus className="w-4 h-4" />
                    Создать кампанию
                  </button>
                  <button
                    type="button"
                    onClick={() => setGroupingModalOpen(true)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-gray-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
                  >
                    <HiFilter className="w-4 h-4 text-gray-500" />
                    Группировка строк
                  </button>
                  <button
                    type="button"
                    onClick={() => setTableMetricsModalOpen(true)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-gray-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
                  >
                    <HiChartBar className="w-4 h-4 text-gray-500" />
                    Метрики таблицы
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeatmapModalOpen(true)}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-gray-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
                  >
                    <HiChartBar className="w-4 h-4 text-gray-500" />
                    Тепловая карта
                  </button>
                  <button
                    type="button"
                    onClick={() => setAutomationModalOpen(true)}
                    disabled={!budgetInfo?.budget}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-gray-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                    title={!budgetInfo?.budget ? 'Сначала задайте месячный бюджет' : 'Настройки автоматизации'}
                  >
                    <HiCog className="w-4 h-4 text-gray-500" />
                    Автоматизация{automationEnabled ? '' : ': выкл'}
                  </button>
                </div>
              </div>
              <span className="text-sm text-gray-500">{filteredCampaignRows.length} кампаний</span>
            </div>
            <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 pl-2 [scrollbar-gutter:stable]">
              <table className="w-full text-sm border-separate border-spacing-0">
                <colgroup>
                  <col style={{ width: PINNED_COL_WIDTHS[0] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[1] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[2] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[3] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[4] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[5] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[6] }} />
                  <col style={{ width: PINNED_COL_WIDTHS[7] }} />
                  <col className="min-w-[5rem]" />
                  <col className="min-w-[4.5rem]" />
                  <col className="min-w-[5.5rem]" />
                  <col className="min-w-[4rem]" />
                  <col className="min-w-[5.5rem]" />
                  <col className="min-w-[4rem]" />
                  <col className="min-w-[4rem]" />
                  <col className="min-w-[4rem]" />
                  <col className="min-w-[5.5rem]" />
                  <col className="min-w-[5.5rem]" />
                  <col className="min-w-[4.5rem]" />
                  <col className="min-w-[4.5rem]" />
                  <col className="w-24" />
                </colgroup>
                <thead className="border-b-2 border-slate-200/80 sticky top-0 z-10 bg-white/95 backdrop-blur-sm">
                  <tr>
                    <th {...pinnedCellProps(0, 'py-3 px-3 bg-white/95 text-left text-xs font-normal uppercase text-gray-500', { header: true })}>
                      <input
                        type="checkbox"
                        checked={selectedCampaigns.size === filteredCampaignRows.length && filteredCampaignRows.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCampaigns(new Set(filteredCampaignRows.map(c => c.advert_id)));
                          } else {
                            setSelectedCampaigns(new Set());
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        title={selectedCampaigns.size === campaignRows.length ? 'Снять все с графика' : 'Выбрать все для графика'}
                      />
                    </th>
                    <th {...pinnedCellProps(1, 'py-3 px-4 bg-white/95 text-left text-xs font-normal uppercase text-gray-500', { header: true })}>
                      <TableColumnFilter
                        label="Кампания"
                        active={isCampaignFilterActive}
                        open={openColumnFilter === 'campaign'}
                        onToggle={() => setOpenColumnFilter((v) => {
                          if (v === 'campaign') return null;
                          setCampaignFilterDraft(campaignFilterApplied);
                          return 'campaign';
                        })}
                      >
                        <textarea
                          value={campaignFilterDraft}
                          onChange={(e) => setCampaignFilterDraft(e.target.value)}
                          rows={5}
                          placeholder={'Название, № кампании,\nартикул WB или продавца\n(список через Enter)'}
                          className="w-full mb-2 border border-gray-300 rounded-lg px-2 py-2 text-xs bg-white focus:ring-2 focus:ring-gray-300 focus:border-gray-400 resize-none"
                        />
                        <div className="text-[11px] text-gray-500 mb-2">
                          {campaignFilterDraftTokens.length || campaignFilterTokens.length
                            ? `Задано: ${campaignFilterDraftTokens.length || campaignFilterTokens.length}, найдено: ${filteredCampaignFilterOptions.length}`
                            : 'Фильтр не задан'}
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {filteredCampaignFilterOptions.map((c) => (
                            <label key={c.id} className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={isCampaignFilterChecked(c.id)}
                                onChange={() => toggleCampaignFilterId(c.id)}
                                className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="leading-snug">
                                <span className="block">{c.name}</span>
                                <span className="text-gray-400">ID {c.id}</span>
                              </span>
                            </label>
                          ))}
                          {filteredCampaignFilterOptions.length === 0 && (
                            <div className="text-xs text-gray-400 px-1 py-2">Ничего не найдено</div>
                          )}
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-2">
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                            onClick={resetCampaignFilter}
                            disabled={!campaignFilterDraft && !campaignFilterApplied && campaignFilterIds === null}
                          >
                            Очистить
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                            onClick={() => applyCampaignTextFilter(campaignFilterDraft, { close: true })}
                          >
                            Применить
                          </button>
                        </div>
                      </TableColumnFilter>
                    </th>
                    <th {...pinnedCellProps(2, 'py-3 px-4 bg-white/95 text-left text-xs font-normal uppercase text-gray-500', { header: true })}>
                      <TableColumnFilter
                        label="Тип"
                        active={isTypeFilterActive}
                        open={openColumnFilter === 'type'}
                        onToggle={() => setOpenColumnFilter((v) => (v === 'type' ? null : 'type'))}
                      >
                        <div className="space-y-1">
                          {typeFilterOptions.map((label) => (
                            <label key={label} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={isLabelFilterChecked(typeFilter, typeFilterOptions, label)}
                                onChange={() => toggleLabelFilter(setTypeFilter, typeFilterOptions, typeFilter, label)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setTypeFilter(null)}
                          className="mt-2 text-xs text-indigo-600 hover:text-indigo-700"
                        >
                          Сбросить
                        </button>
                      </TableColumnFilter>
                    </th>
                    <th {...pinnedCellProps(3, 'py-3 px-4 bg-white/95 text-left text-xs font-normal uppercase text-gray-500', { header: true })}>
                      <TableColumnFilter
                        label="Оплата"
                        active={isPaymentFilterActive}
                        open={openColumnFilter === 'payment'}
                        onToggle={() => setOpenColumnFilter((v) => (v === 'payment' ? null : 'payment'))}
                      >
                        <div className="space-y-1">
                          {paymentFilterOptions.map((label) => (
                            <label key={label} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={isLabelFilterChecked(paymentFilter, paymentFilterOptions, label)}
                                onChange={() => toggleLabelFilter(setPaymentFilter, paymentFilterOptions, paymentFilter, label)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setPaymentFilter(null)}
                          className="mt-2 text-xs text-indigo-600 hover:text-indigo-700"
                        >
                          Сбросить
                        </button>
                      </TableColumnFilter>
                    </th>
                    <th {...pinnedCellProps(4, 'py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500', { header: true })}>
                      Ставки
                    </th>
                    <th {...pinnedCellProps(5, 'py-3 px-4 bg-white/95 text-left text-xs font-normal uppercase text-gray-500', { header: true })}>
                      <TableColumnFilter
                        label="Статус"
                        active={isStatusFilterActive}
                        open={openColumnFilter === 'status'}
                        onToggle={() => setOpenColumnFilter((v) => (v === 'status' ? null : 'status'))}
                      >
                        <div className="space-y-1">
                          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={visibleStatusBuckets.includes('active')}
                              onChange={() => toggleStatusBucket('active')}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>Запущено ({statusCounters.active})</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={visibleStatusBuckets.includes('paused')}
                              onChange={() => toggleStatusBucket('paused')}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>Пауза ({statusCounters.paused})</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={visibleStatusBuckets.includes('completed')}
                              onChange={() => toggleStatusBucket('completed')}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>Завершена ({statusCounters.completed})</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => setVisibleStatusBuckets(['active', 'paused', 'completed'])}
                          className="mt-2 text-xs text-indigo-600 hover:text-indigo-700"
                        >
                          Сбросить
                        </button>
                      </TableColumnFilter>
                    </th>
                    <th {...pinnedCellProps(6, 'py-3 px-4 bg-white/95 text-left text-xs font-normal uppercase text-gray-500', { header: true })}>
                      Бюджет и стратегия
                    </th>
                    <th {...pinnedCellProps(7, 'py-3 px-4 bg-white/95 text-center align-middle text-xs font-normal uppercase text-gray-500', { header: true })}>
                      <button
                        type="button"
                        onClick={() => handleSort('promoted_stock_remain')}
                        className="flex w-full items-center justify-center gap-1 hover:text-gray-800 transition-colors"
                        title="Остаток на WB по артикулу с наибольшим расходом в статистике"
                      >
                        <span>Остаток</span>
                        {sortColumn === 'promoted_stock_remain' && (
                          sortDirection === 'desc'
                            ? <HiArrowDown className="w-4 h-4 text-blue-600" />
                            : <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )}
                      </button>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('views') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('impressions')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Показы</span>
                          {sortColumn === 'impressions' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('views')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('views')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('views') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('views') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('clicks') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('clicks')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Клики</span>
                          {sortColumn === 'clicks' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('clicks')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('clicks')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('clicks') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('clicks') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('spend') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('spend')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Расход</span>
                          {sortColumn === 'spend' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('spend')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('spend')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('spend') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('spend') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('orders') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('orders')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Заказы</span>
                          {sortColumn === 'orders' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('orders')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('orders')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('orders') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('orders') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th
                      className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('direct_orders') ? '' : 'hidden'}`}
                      title="Заказы по продвигаемому артикулу"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('direct_orders')}
                        className="flex items-center justify-end gap-1 w-full hover:text-gray-800 transition-colors"
                      >
                        <span>Прямые заказы</span>
                        {sortColumn === 'direct_orders' && (
                          sortDirection === 'desc' ? (
                            <HiArrowDown className="w-4 h-4 text-blue-600" />
                          ) : (
                            <HiArrowUp className="w-4 h-4 text-blue-600" />
                          )
                        )}
                      </button>
                    </th>
                    <th
                      className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('associated_orders') ? '' : 'hidden'}`}
                      title="Остальные заказы: другие артикулы и заказы без привязки к артикулу"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('associated_orders')}
                        className="flex items-center justify-end gap-1 w-full hover:text-gray-800 transition-colors"
                      >
                        <span>Ассоциированные заказы</span>
                        {sortColumn === 'associated_orders' && (
                          sortDirection === 'desc' ? (
                            <HiArrowDown className="w-4 h-4 text-blue-600" />
                          ) : (
                            <HiArrowUp className="w-4 h-4 text-blue-600" />
                          )
                        )}
                      </button>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('atbs') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('atbs')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>В корзину</span>
                          {sortColumn === 'atbs' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('atbs')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('atbs')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('atbs') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('atbs') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('revenue') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('revenue')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Доход</span>
                          {sortColumn === 'revenue' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('revenue')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('revenue')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('revenue') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('revenue') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th
                      className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('direct_revenue') ? '' : 'hidden'}`}
                      title="Доход по продвигаемому артикулу"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('direct_revenue')}
                        className="flex items-center justify-end gap-1 w-full hover:text-gray-800 transition-colors"
                      >
                        <span>Прямой доход</span>
                        {sortColumn === 'direct_revenue' && (
                          sortDirection === 'desc' ? (
                            <HiArrowDown className="w-4 h-4 text-blue-600" />
                          ) : (
                            <HiArrowUp className="w-4 h-4 text-blue-600" />
                          )
                        )}
                      </button>
                    </th>
                    <th
                      className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('associated_revenue') ? '' : 'hidden'}`}
                      title="Остальной доход: другие артикулы и доход без привязки к артикулу"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('associated_revenue')}
                        className="flex items-center justify-end gap-1 w-full hover:text-gray-800 transition-colors"
                      >
                        <span>Ассоциированный доход</span>
                        {sortColumn === 'associated_revenue' && (
                          sortDirection === 'desc' ? (
                            <HiArrowDown className="w-4 h-4 text-blue-600" />
                          ) : (
                            <HiArrowUp className="w-4 h-4 text-blue-600" />
                          )
                        )}
                      </button>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('ctr') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('ctr')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>CTR</span>
                          {sortColumn === 'ctr' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('ctr')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('ctr')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('ctr') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('ctr') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('cpc') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('cpc')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>CPC</span>
                          {sortColumn === 'cpc' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('cpc')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('cpc')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('cpc') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('cpc') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('cpo') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('cpo')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>CPO</span>
                          {sortColumn === 'cpo' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('cpo')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('cpo')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('cpo') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('cpo') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('cart_cost') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('cart_cost')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Цена корзины</span>
                          {sortColumn === 'cart_cost' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('cart_cost')}
                          className={`p-1 rounded transition-colors ${
                            selectedMetrics.includes('cart_cost')
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={selectedMetrics.includes('cart_cost') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('cart_cost') ? (
                            <HiX className="w-4 h-4" />
                          ) : (
                            <HiChartBar className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('avg_position') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('avg_position')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>Позиция</span>
                          {sortColumn === 'avg_position' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('avg_position')}
                          className={`p-1 rounded transition-colors ${selectedMetrics.includes('avg_position') ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                          title={selectedMetrics.includes('avg_position') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('avg_position') ? <HiX className="w-4 h-4" /> : <HiChartBar className="w-4 h-4" />}
                        </button>
                      </div>
                    </th>
                    <th className={`py-3 px-4 bg-white/95 text-right text-xs font-normal uppercase text-gray-500 ${isTableMetricVisible('drr') ? '' : 'hidden'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort('drr')}
                          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
                        >
                          <span>ДРР</span>
                          {sortColumn === 'drr' && (
                            sortDirection === 'desc' ? (
                              <HiArrowDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <HiArrowUp className="w-4 h-4 text-blue-600" />
                            )
                          )}
                        </button>
                        <button
                          onClick={() => handleMetricToggle('drr')}
                          className={`p-1 rounded transition-colors ${selectedMetrics.includes('drr') ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                          title={selectedMetrics.includes('drr') ? 'Убрать с графика' : 'Добавить на график'}
                        >
                          {selectedMetrics.includes('drr') ? <HiX className="w-4 h-4" /> : <HiChartBar className="w-4 h-4" />}
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/70">
                  {tableBodyRows}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Модалки биддера: бюджет, стратегия, режим показов, массовое редактирование */}
      {strategyModalOpen && selectedCampaignForStrategy && (
        <StrategyModal
          campaign={selectedCampaignForStrategy}
          isOpen={strategyModalOpen}
          onClose={() => { setStrategyModalOpen(false); setSelectedCampaignForStrategy(null); }}
          onSave={saveCampaignStrategy}
          clientId={clientId}
        />
      )}
      {groupingModalOpen && (
        <ModalOverlay onClick={() => setGroupingModalOpen(false)}>
          <div
            className="modal-panel rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Группировка строк</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Перетащите блоки, чтобы изменить порядок вложенности.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGroupingModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Закрыть"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-indigo-700/90 mb-4">
              Порядок задаёт уровни вложенности: раскрытие строки показывает следующие измерения (кампания → плейсмент → запрос и т.д.).
            </p>
            <div className="flex flex-wrap gap-2">
              {tableGroupOrder.map((key) => {
                const option = tableGroupingOptions.find((o) => o.key === key);
                return (
                  <button
                    key={key}
                    type="button"
                    draggable
                    onDragStart={() => setDragGroupingKey(key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      moveGrouping(dragGroupingKey, key);
                      setDragGroupingKey(null);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-gray-700 cursor-move"
                  >
                    <span className="text-slate-400">⋮⋮</span>
                    <span>{option?.label || key}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setGroupingModalOpen(false)}
                className="glass-btn px-4 py-2 text-sm font-medium"
              >
                Готово
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {tableMetricsModalOpen && (
        <ModalOverlay onClick={() => setTableMetricsModalOpen(false)}>
          <div
            className="modal-panel rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Метрики таблицы</h3>
                <p className="text-sm text-gray-600 mt-1">Выберите столбцы для отображения в таблице кампаний.</p>
              </div>
              <button
                type="button"
                onClick={() => setTableMetricsModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Закрыть"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {tableMetricOptions.map((m) => (
                <label
                  key={m.key}
                  className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={isTableMetricVisible(m.key)}
                    onChange={() => toggleTableMetricVisibility(m.key)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{m.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setTableMetricsModalOpen(false)}
                className="glass-btn px-4 py-2 text-sm font-medium"
              >
                Готово
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {budgetModalOpen && (
        <MonthlyBudgetModal
          isOpen={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          clientId={clientId}
          onUpdate={refreshBidderDataInBackground}
        />
      )}
      <CreateCampaignModal
        open={createCampaignModalOpen}
        onClose={() => setCreateCampaignModalOpen(false)}
        initialRows={[]}
        clientId={clientId}
        onSuccess={() => {
          loadCampaignsList();
          fetchSummary(false);
        }}
      />
      {automationModalOpen && (
        <AutomationControlModal
          isOpen={automationModalOpen}
          onClose={() => setAutomationModalOpen(false)}
          clientId={clientId}
          month={budgetMonth}
          onSaved={loadBudgetInfo}
        />
      )}
      <ScheduleModal
        campaign={selectedCampaignForSchedule}
        isOpen={scheduleModalOpen}
        onClose={() => { setScheduleModalOpen(false); setSelectedCampaignForSchedule(null); }}
        clientId={clientId}
      />
      {selectedCampaigns.size > 0 && (
        <BulkEditPanel
          selectedCampaignIds={Array.from(selectedCampaigns)}
          campaigns={campaignRows}
          onClose={() => setSelectedCampaigns(new Set())}
          onUpdate={() => { refreshBidderDataInBackground(); setSelectedCampaigns(new Set()); }}
          clientId={clientId}
        />
      )}

      {heatmapModalOpen && (
        <ModalOverlay onClick={() => setHeatmapModalOpen(false)}>
          <div
            className="modal-panel rounded-2xl w-full max-w-[1120px] h-[82vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header flex justify-between items-center px-5 py-3 rounded-t-2xl border-b border-white/40">
              <h3 className="text-base font-semibold text-gray-800">Тепловая карта</h3>
              <button
                onClick={() => setHeatmapModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-auto p-4 flex flex-col items-start min-w-0">
              <HeatmapPage clientId={clientId} embedded />
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Модальное окно выбора дат */}
      {dateModalOpen && (
        <ModalOverlay onClick={() => setDateModalOpen(false)}>
          <div
            className="modal-panel rounded-2xl max-w-5xl w-full mx-4 my-8 flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Заголовок */}
            <div className="modal-header flex justify-between items-center p-6 rounded-t-2xl">
              <h3 className="text-xl font-semibold text-gray-800">Выбор периода</h3>
              <button
                onClick={() => setDateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Основной контент */}
            <div className="flex flex-1 overflow-hidden">
              {/* Левая панель - предустановленные периоды */}
              <div className="w-64 border-r border-white/30 p-6 overflow-y-auto">
                <div className="space-y-1 mb-6">
                  {[
                    { label: 'Вчера', days: 1 },
                    { label: 'Последние 7 дней', days: 7, highlight: true },
                    { label: 'Последние 14 дней', days: 14 },
                    { label: 'Последние 30 дней', days: 30 },
                    { label: 'Последние 60 дней', days: 60 },
                    { label: 'Последние 90 дней', days: 90 },
                    { label: 'Последние 6 месяцев', months: 6 },
                    { label: 'Последние 12 месяцев', months: 12 },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => {
                        const to = new Date();
                        const from = new Date(to);
                        if (preset.months) from.setMonth(from.getMonth() - preset.months);
                        else from.setDate(from.getDate() - preset.days);
                        applyTempMainPeriod(formatDateInput(from), formatDateInput(to));
                      }}
                      className={`w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors ${
                        preset.highlight ? 'flex items-center gap-2' : ''
                      }`}
                    >
                      {preset.highlight && <HiCheck className="w-4 h-4 text-purple-600" />}
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-700">Сравнение</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showCompareLines}
                        onChange={(e) => setShowCompareLines(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {showCompareLines && (
                    <div className="space-y-1">
                      {COMPARE_PRESET_OPTIONS.map((opt) => {
                        const active = tempComparePreset === opt.key && !tempCompareDirty;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => applyTempComparePreset(opt.key)}
                            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                              active
                                ? 'bg-indigo-50 text-indigo-800'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <span className="w-4 shrink-0">
                              {active ? <HiCheck className="w-4 h-4 text-indigo-600" /> : null}
                            </span>
                            <span className="font-medium">{opt.label}</span>
                          </button>
                        );
                      })}
                      {tempComparePreset === 'manual' && (
                        <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                          <HiCheck className="w-4 h-4 text-slate-500" />
                          Задан вручную на календаре
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Правая панель - календари и поля ввода */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-6">
                  {/* Поля ввода дат */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Основной: от</label>
                      <input
                        type="date"
                        value={tempPeriodFrom}
                        onChange={(e) => {
                          const fromStr = e.target.value;
                          setTempPeriodFrom(fromStr);
                          setSelectingPeriod('main');
                          if (fromStr && tempPeriodTo && fromStr <= tempPeriodTo) {
                            applyTempMainPeriod(fromStr, tempPeriodTo);
                          } else {
                            setTempCompareDirty(false);
                            if (tempComparePreset === 'manual') setTempComparePreset('previous');
                          }
                        }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Основной: до</label>
                      <input
                        type="date"
                        value={tempPeriodTo}
                        onChange={(e) => {
                          const toStr = e.target.value;
                          setTempPeriodTo(toStr);
                          if (tempPeriodFrom && toStr && tempPeriodFrom <= toStr) {
                            applyTempMainPeriod(tempPeriodFrom, toStr);
                          } else {
                            setSelectingPeriod('main');
                            setTempCompareDirty(false);
                            if (tempComparePreset === 'manual') setTempComparePreset('previous');
                          }
                        }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {showCompareLines && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Сравнить от</label>
                        <input
                          type="date"
                          value={tempCompareFrom}
                          onChange={(e) => {
                            setTempCompareFrom(e.target.value);
                            setTempCompareDirty(true);
                            setTempComparePreset('manual');
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Сравнить до</label>
                        <input
                          type="date"
                          value={tempCompareTo}
                          onChange={(e) => {
                            setTempCompareTo(e.target.value);
                            setTempCompareDirty(true);
                            setTempComparePreset('manual');
                            setSelectingPeriod('main');
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {/* Календарь */}
                  <DateRangeCalendar
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    periodFrom={tempPeriodFrom}
                    periodTo={tempPeriodTo}
                    compareFrom={tempCompareFrom}
                    compareTo={tempCompareTo}
                    showCompare={showCompareLines}
                    onDateClick={handleCalendarDateClick}
                    selectingPeriod={selectingPeriod}
                  />
                </div>
              </div>
            </div>

            {/* Футер с кнопками */}
            <div className="modal-footer flex justify-end gap-3 p-6 rounded-b-2xl">
              <button
                onClick={() => setDateModalOpen(false)}
                className="glass-btn-secondary px-6 py-2 font-medium"
              >
                ОТМЕНА
              </button>
              <button
                onClick={handleDateApply}
                className="glass-btn px-6 py-2 font-medium"
              >
                ПРИМЕНИТЬ
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

